import ScoreInputModal from './ScoreInputModal';
import notify from './utils/notify';
import HandicapOverrideModal from './HandicapOverrideModal';
import HandicapKeypadModal from './components/HandicapKeypadModal';
import HandicapEditChoiceModal from './components/HandicapEditChoiceModal';
import HandicapAdjustmentConfirmationModal from './HandicapAdjustmentConfirmationModal';
import WhatsAppShareModal from './WhatsAppShareModal';
import StartingHoleModal from './components/StartingHoleModal';
import CourseStatsEditModal from './components/CourseStatsEditModal';
import ScorecardOCRModal from './components/ScorecardOCRModal';
import ChampionshipPairingsModal from './components/ChampionshipPairingsModal';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './NewScorecardEntry.css';
import './ScorecardControlCentre.css';
// Last, so the shared marks win the cascade over anything this page's own
// stylesheets still say about .score-display.
import ScoreMarker from './components/ScoreMarker';
import ScoreSummary from './components/ScoreSummary';
import { countResults } from './utils/scoreResult';
import FullScorecardGrid from './components/FullScorecardGrid';
import FullScorecardTotals from './components/FullScorecardTotals';
import BottomNav from './BottomNav';
import MainMenu from './MainMenu';
import { 
  FiBarChart2,
  FiSettings,
  FiGrid,
  FiCamera,
  FiEdit as FiEditIcon,
  FiCheck,
  FiAlertCircle,
  FiChevronDown, 
  FiX, 
  FiChevronRight
} from 'react-icons/fi';
import { BsStars } from 'react-icons/bs';
import { useQuery, useQueryClient } from 'react-query';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { Permission } from './permissions';
import { isPlayerInRoster, myPlayerIdIn } from './utils/societyPlayer';
import HeaderProfile from './components/HeaderProfile';
import SocietyHeaderBadge from './components/SocietyHeaderBadge';
import TournamentSelectorModal from './components/TournamentSelectorModal';
import CourseStatsBar from './components/CourseStatsBar';
import TeamRoundPanel from './TeamRoundPanel';
import { stablefordPoints, groupPlayersByTee } from './utils/golf';
import { isMatchplayFormat } from './utils/matchplay';
import { getTeeColor, teeNeedsOutline } from './utils/teeColors';

const getHandicapStrokes = (playingHandicap, strokeIndex) => {
  if (playingHandicap === null || playingHandicap === undefined || strokeIndex === null || strokeIndex === undefined) return 0;
  let handicapStrokes = 0;
  if (playingHandicap > 0) {
    const fullRounds = Math.floor(playingHandicap / 18);
    const remainingStrokes = playingHandicap % 18;
    handicapStrokes += fullRounds;
    if (strokeIndex <= remainingStrokes) {
      handicapStrokes += 1;
    }
  } else if (playingHandicap < 0) {
    const absHandicap = Math.abs(playingHandicap);
    const fullRounds = Math.floor(absHandicap / 18);
    const remainingStrokes = absHandicap % 18;
    handicapStrokes -= fullRounds;
    if (strokeIndex > (18 - remainingStrokes)) {
      handicapStrokes -= 1;
    }
  }
  return handicapStrokes;
};

const calculatePoints = (grossScore, par, strokeIndex, playingHandicap) => {
  if (!grossScore || isNaN(grossScore) || grossScore === 0 || !par || !strokeIndex) {
    return '';
  }
  // Shared with the server / live / offline surfaces — includes the 6-point cap.
  return stablefordPoints(par, playingHandicap, strokeIndex, grossScore);
};

// The label cell of a Par row when the round spans several tees. It replaces
// the word "Par" with the tee whose card that row states, because with more
// than one section on screen "Par" alone no longer identifies anything.
const TeeRowLabel = ({ tee }) => (
  <span className="par-row-tee">
    <span
      className={`par-row-tee__dot${teeNeedsOutline(tee?.name) ? ' par-row-tee__dot--outlined' : ''}`}
      style={{ backgroundColor: getTeeColor(tee?.name) || '#ffffff' }}
    />
    <span className="par-row-tee__name">{tee?.name || 'Par'}</span>
  </span>
);

// One hole of the landscape grid. The marker is the shared ScoreMarker — the
// same component the portrait card (FullScorecardGrid) and the leaderboard's
// hole-by-hole card draw — so a round looks the same wherever it is opened.
const ScoreCell = React.memo(({ player, holeNumber, score, par, onClick, className = '', readOnly = false }) => {
  const cellClass = `grid-cell score-cell ${className} ${readOnly ? 'score-cell-readonly' : ''}`;

  return (
    <div className={cellClass} onClick={readOnly ? undefined : () => onClick(player, holeNumber)}>
      <ScoreMarker strokes={score} par={par} className="score-display" />
    </div>
  );
});

const PointsCell = React.memo(({ score, par, strokeIndex, playingHandicap, isMobile, className = '' }) => {
  const points = calculatePoints(score, par, strokeIndex, playingHandicap);
  const cellClass = isMobile ? `grid-cell points-cell ${className}` : `grid-cell desktop-points-cell ${className}`;
  return (
    <div className={cellClass}>
      {points !== '' ? String(points) : '-'}
    </div>
  );
});

const SyncActionFooter = ({ syncState, onRecalculate, onEndRound, onReopen, lastSyncedAt }) => {
  // Sentence case, to match every other button in the app. The e2e spec matches
  // these case-insensitively, so it is unaffected.
  const btnLabel = {
    OUT_OF_SYNC: 'Re-calculate scores',
    SYNCING: 'Syncing…',
    SYNCED: 'Scores updated',
    READY: 'End round',
    FINALIZED: 'Re-open round',
  }[syncState];

  const handleClick =
    syncState === 'OUT_OF_SYNC' ? onRecalculate :
    syncState === 'READY'       ? onEndRound :
    syncState === 'FINALIZED'   ? onReopen :
    undefined;

  return (
    <div className="sync-action-footer">
      {syncState === 'OUT_OF_SYNC' && (
        <div className="sync-footer-banner">
          <span className="sync-footer-banner-dot" />
          <span>Course or handicap data has changed.</span>
        </div>
      )}
      <button
        key={syncState}
        className={`sync-footer-btn sync-footer-btn--${syncState.toLowerCase().replace('_', '-')}`}
        onClick={handleClick}
        disabled={syncState === 'SYNCING' || syncState === 'SYNCED'}
      >
        <span className="sync-footer-btn-label">
          {syncState === 'SYNCING' && <span className="sync-footer-spinner" />}
          {syncState === 'SYNCED' && <FiCheck size={16} strokeWidth={3} />}
          {btnLabel}
        </span>
      </button>
      {syncState === 'READY' && lastSyncedAt && (
        <p className="sync-footer-timestamp">Last verified: {lastSyncedAt}</p>
      )}
    </div>
  );
};

const FinalizeConfirmModal = ({ isOpen, onConfirm, onCancel, applyAdjustments, isMatchplay, isFinalizing }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onCancel} style={{ zIndex: 11000 }}>
      <div className="modal-content-bottom-sheet" onClick={e => e.stopPropagation()} style={{ padding: '24px' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '16px', color: '#1e293b', textAlign: 'center' }}>Finalise round?</h2>
        
        <ul style={{ margin: '0 0 24px 0', padding: '0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '0.95rem' }}>
            <FiCheck color="#22c55e" /> Lock all scores
          </li>
          <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: (applyAdjustments && !isMatchplay) ? '#64748b' : '#94a3b8', fontSize: '0.95rem' }}>
            {(applyAdjustments && !isMatchplay) ? <FiCheck color="#22c55e" /> : <FiX color="#94a3b8" />}
            {isMatchplay
              ? 'No handicap adjustments — matchplay'
              : (applyAdjustments ? 'Apply handicap adjustments' : 'Skip handicap adjustments')}
          </li>
        </ul>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button 
            className="btn-primary" 
            onClick={onConfirm}
            disabled={isFinalizing}
            style={{ width: '100%', height: '48px', borderRadius: '12px', fontWeight: 600 }}
          >
            {isFinalizing ? 'Finalising...' : 'Confirm'}
          </button>
          <button 
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontWeight: 600, padding: '12px', cursor: 'pointer' }}
            onClick={onCancel}
            disabled={isFinalizing}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const NewScorecardEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { authenticatedFetch } = useApi();
  const { currentSociety, hasPermission, userProfile, isSocietyOwner } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const tournamentParam = queryParams.get('tournament');
  const roundParam = queryParams.get('round');

  const [selectedTournament, setSelectedTournament] = useState(() => {
    if (tournamentParam) {
      const parsed = parseInt(tournamentParam, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return location.state?.tournamentId || '';
  });
  const [selectedRound, setSelectedRound] = useState(() => {
    if (roundParam) {
      const parsed = parseInt(roundParam, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return 1;
  });
  const [scores, setScores] = useState({});
  const [customHandicaps, setCustomHandicaps] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [isHandicapModalOpen, setIsHandicapModalOpen] = useState(false);
  const [hcpKeypadPlayer, setHcpKeypadPlayer] = useState(null);
  const [hcpEditMode, setHcpEditMode] = useState('playing'); // 'playing' | 'index'
  const [hcpChoicePlayer, setHcpChoicePlayer] = useState(null); // shows the choice modal
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcSuccess, setRecalcSuccess] = useState(false);
  const [isHandicapAdjustmentModalOpen] = useState(false);
  const [isStartingHoleModalVisible, setIsStartingHoleModalVisible] = useState(false);
  const [isCourseStatsModalOpen, setIsCourseStatsModalOpen] = useState(false);
  const [handicapAdjustmentModalData, setHandicapAdjustmentModalData] = useState([]);
  const [isWhatsAppShareModalOpen, setIsWhatsAppShareModalOpen] = useState(false);
  const [selectedCellData, setSelectedCellData] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.matchMedia('(max-width: 768px)').matches);
  const [isMobileLandscape, setIsMobileLandscape] = useState(
    window.matchMedia('(orientation: landscape) and (max-height: 500px) and (max-width: 932px)').matches
  );
  const [isPageReady, setIsPageReady] = useState(false);
  const [roundsInReview, setRoundsInReview] = useState([]); // Array of round numbers currently in review phase
  const [isTournamentSelectorOpen, setIsTournamentSelectorOpen] = useState(false);
  const [viewMode, setViewMode] = useState(location.state?.viewMode || null); // 'junction', 'grid', or 'players'
  // Which nine the Finalise body is showing. 'total' swaps the grid for the
  // ranked check table; it is not a third nine.
  const [nineTab, setNineTab] = useState('front');
  const [applyAdjustments, setApplyAdjustments] = useState(true);
  const [isFinalizeConfirmOpen, setIsFinalizeConfirmOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [championshipPairingsData, setChampionshipPairingsData] = useState(null);
  const [nextRoundPairingsData, setNextRoundPairingsData] = useState(null);

  const isReviewingAdjustments = useMemo(() => roundsInReview.includes(selectedRound), [roundsInReview, selectedRound]);

  // --- Seamless Entry Logic ---
  useEffect(() => {
    const timer = setTimeout(() => setIsPageReady(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // OCR State
  const [isOCRModalOpen, setIsOCRModalOpen] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Queries using authenticatedFetch
  const { data: tournaments = [], isLoading: isTournamentsLoading } = useQuery(['tournaments', currentSociety?.id], async () => {
    if (!currentSociety) return [];
    const res = await authenticatedFetch('/tournaments?limit=10');
    return res.json();
  }, { enabled: !!currentSociety });

  const { data: roundOptions = [] } = useQuery(['courses', selectedTournament], async () => {
    if (!selectedTournament) return [];
    const res = await authenticatedFetch(`/tournaments/${selectedTournament}/courses`);
    return res.json();
  }, { enabled: !!selectedTournament });

  // YOUR latest competition, not the society's. The default used to be the
  // newest tournament in the society — someone else's roll up if it was dated
  // after yours. A link (?tournament= or navigation state) still wins.
  const { data: myLatest = [], isFetched: myLatestFetched } = useQuery(
    ['tournaments', 'mine-latest', currentSociety?.id], async () => {
      const res = await authenticatedFetch('/tournaments?mine=true&limit=1');
      return res.ok ? res.json() : [];
    }, { enabled: !!currentSociety && !selectedTournament });

  useEffect(() => {
    if (selectedTournament || !myLatestFetched) return;
    if (myLatest.length > 0) {
      setSelectedTournament(myLatest[0].id);
    } else if (tournaments.length > 0) {
      // Never played in one (an organiser running the society): the newest
      // is the only sensible place to start.
      setSelectedTournament(tournaments[0].id);
    }
  }, [myLatest, myLatestFetched, tournaments, selectedTournament]);

  useEffect(() => {
    if (roundOptions.length > 0) {
      // If we already have a round from the URL/State, don't override it
      if (roundParam) return;

      // Once every round is finalised there is no "open" round; fall back to
      // the LAST round (the finale / results), never bounce to Round 1 — which
      // otherwise happens the moment the final round is finalised.
      const lastRound = roundOptions.reduce(
        (a, b) => (b.sequence_number > a.sequence_number ? b : a),
        roundOptions[0]
      );
      const openRound = roundOptions.find(r => r.is_started && !r.is_finalized) ||
                        roundOptions.find(r => !r.is_started) ||
                        lastRound;

      if (openRound) {
        setSelectedRound(openRound.sequence_number);
      }
    }
  }, [roundOptions, roundParam]);

  const { data: roundData, isLoading } = useQuery(['roundData', selectedTournament, selectedRound], async () => {
    if (!selectedTournament) return null;
    const res = await authenticatedFetch(`/tournaments/${selectedTournament}/round-data?round_number=${selectedRound}`);
    const data = await res.json();
    return data;
  }, { enabled: !!selectedTournament, keepPreviousData: true });

  // Trigger ready state once all primary data is loaded
  useEffect(() => {
    if (!isTournamentsLoading && tournaments && !isLoading && roundData !== undefined) {
        setIsPageReady(true);
    }
  }, [isTournamentsLoading, tournaments, isLoading, roundData]);

  // A MATCH IS NOT SUBJECT TO HANDICAP ADJUSTMENT. Holes are conceded and a
  // match ends when it is won, so the Stableford total the rules read is a
  // total over holes some of which were never played. The server refuses to
  // apply them to a matchplay tournament whatever it is sent
  // (`scoring.py::end_round`); this is so the organiser is never SHOWN a cut
  // that finalising will then decline to make.
  const isMatchplayRound = isMatchplayFormat(roundData?.tournament?.format);
  const allPlayers = useMemo(() => roundData?.players || [], [roundData]);
  const roundPlayerHandicaps = useMemo(() => roundData?.round_player_handicaps || {}, [roundData]);
  const isCurrentRoundFinalized = roundData?.is_finalized || false;

  // ─── Competition report ───────────────────────────────────────────────
  // The report states scores and index revisions as fact, so it is offered
  // only once EVERY round is finalised — not merely the one on screen.
  // What the server says can be produced right now: the competition report
  // (`code === 'ok'`) and the list of rounds that are finalised.
  //
  // THE ROW IS SHOWN OR IT IS NOT — there is no disabled state. A row that
  // cannot be used is an advert wearing the clothes of a control, and it made
  // the page look broken while a round was still being scored.
  const [reportStatus, setReportStatus] = useState(null);
  const [isBuildingReport, setIsBuildingReport] = useState(false);

  // Which rounds this page believes are finalised, as a stable string. The
  // effect below re-asks the server when it changes.
  const finalisedSignature = useMemo(
    () => roundOptions.filter((r) => r.is_finalized)
      .map((r) => r.sequence_number).join(','),
    [roundOptions],
  );

  // Asked whenever a competition is selected, NOT only once it is finalised.
  // The disabled row exists precisely for the unfinalised state, so gating the
  // question on finalisation meant the one case it was written for could never
  // be reached. The endpoint is built to be asked this often — it answers
  // yes/no without assembling the report.
  useEffect(() => {
    if (!selectedTournament) {
      setReportStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(`/tournaments/${selectedTournament}/report-availability`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setReportStatus({
            code: data.code,
            finalisedRounds: data.finalised_rounds || [],
          });
        }
      } catch (e) {
        // A report we can't confirm is a report we don't offer.
      }
    })();
    return () => { cancelled = true; };
    // Re-asked whenever the set of FINALISED rounds changes, not merely when
    // the last one lands. Keyed on `allRoundsFinalized` alone, finalising round
    // one of two moved nothing — that flag stays false — so the row for a round
    // that had just been signed off did not appear until the page was reloaded.
  }, [selectedTournament, finalisedSignature, authenticatedFetch]);

  // The whole competition, once every round is signed off.
  const canDownloadCompetition = reportStatus?.code === 'ok';
  // ...and any round that is finalised, which is usually available long before
  // the competition is. `selectedRound` is the round the page is showing, so
  // the row follows the round selector: it appears on a finished round and is
  // simply absent on one still being played.
  const finalisedRounds = useMemo(
    () => reportStatus?.finalisedRounds || [], [reportStatus]);
  const canDownloadThisRound = finalisedRounds.includes(selectedRound);
  // A REPORT IS ABOUT A ROUND THAT IS DONE, so it is only offered while a
  // completed round is in focus. Whichever document is on offer — this round's
  // or the whole competition's — the gate is the same.
  //
  // `canDownloadCompetition` used to short-circuit that: once the competition
  // report existed, the button appeared on every round including ones still
  // being played, which is the opposite of what the note above intends.
  const showReportRow = isCurrentRoundFinalized && (canDownloadThisRound || canDownloadCompetition);

  // Which report to build: `null` for the whole competition, or a round
  // number. Only ever set from the picker, which only appears for a
  // multi-round competition.
  const [reportPicker, setReportPicker] = useState(false);

  const buildReport = useCallback(async (roundNumber) => {
    setIsBuildingReport(true);
    try {
      const qs = roundNumber ? `?round=${roundNumber}` : '';
      const res = await authenticatedFetch(
        `/tournaments/${selectedTournament}/report-data${qs}`);
      const data = await res.json();
      if (!res.ok) {
        notify.error(data.error || 'Could not build the report.');
        return;
      }
      // Dynamic: @react-pdf plus the embedded faces is most of a megabyte, and
      // only this click needs it.
      const { default: downloadCompetitionReport } = await import('./report/downloadCompetitionReport');
      await downloadCompetitionReport(data);
    } catch (e) {
      notify.error('Could not build the report. Please try again.');
    } finally {
      setIsBuildingReport(false);
    }
  }, [authenticatedFetch, selectedTournament]);

  const handleDownloadReport = useCallback(() => {
    if (isBuildingReport) return;

    // The picker only earns its tap when there is genuinely a choice: the
    // competition report plus at least one round, or several finalised rounds.
    // With one option there is nothing to choose between, so it downloads.
    const choices = (canDownloadCompetition ? 1 : 0) + finalisedRounds.length;
    if (choices > 1) {
      setReportPicker(true);
      return;
    }
    buildReport(canDownloadCompetition ? null : selectedRound);
  }, [buildReport, isBuildingReport, canDownloadCompetition, finalisedRounds, selectedRound]);

  // Shared-ball team formats (Foursomes/Greensomes/Texas Scramble) have no
  // individual Round data at all for this round — team_competition/team_rounds
  // come pre-computed (and round_number-scoped) from get_tournament_round_data.
  // isCurrentRoundFinalized/needsRecalculation above already reflect the
  // team-round state when isTeamRoundDay is true (the backend overrides them),
  // so no further branching is needed for those two.
  const teamCompetition = roundData?.team_competition || null;
  const teamRounds = useMemo(() => roundData?.team_rounds || [], [roundData]);
  const isTeamRoundDay = !!teamCompetition;

  const syncState = useMemo(() => {
    if (isCurrentRoundFinalized) return 'FINALIZED';
    if (isRecalculating) return 'SYNCING';
    if (recalcSuccess) return 'SYNCED';
    if (needsRecalculation) return 'OUT_OF_SYNC';
    return 'READY';
  }, [isCurrentRoundFinalized, isRecalculating, recalcSuccess, needsRecalculation]);

  // Finalise tab = the grid scorecard view (viewMode='grid')
  const isOnFinaliseTab = viewMode === 'grid';

  const isTeeSheetFinalized = roundData?.tournament?.is_tee_sheet_finalized || false;
  const isRollUp = roundData?.tournament?.signup_mode === 'roll_up';
  const isRoundCreator = userProfile?.id === roundData?.tournament?.creator_id;
  const isAdmin = currentSociety?.role === 'ADMIN';
  const course = roundData?.course || null;
  const isRoundStarted = isTeamRoundDay ? teamRounds.length > 0 : Object.keys(roundPlayerHandicaps).length > 0;

  // --- Mixed tees -------------------------------------------------------
  // Players in one competition may play from different tees (men's / ladies' /
  // seniors'). Assignments ride along with round-data; anyone without one falls
  // back to the round's own tee, so a single-tee day behaves exactly as before.
  const courseTees = useMemo(() => course?.tees || [], [course]);
  const roundDefaultTeeId = course?.tee_id ?? null;
  const playerTeeAssignments = useMemo(() => {
    const raw = course?.player_tees || {};
    // JSON object keys arrive as strings; the rest of this page keys by number.
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [Number(k), v]));
  }, [course]);
  const teesById = useMemo(
    () => Object.fromEntries(courseTees.map(t => [t.id, t])),
    [courseTees]
  );
  // Which tee the course-stats modal is editing. null means the allowance row
  // was tapped (or the round has a single, unnamed tee) — the modal then shows
  // only the fields that apply.
  const [editingTee, setEditingTee] = useState(null);
  // Whether the modal opens with the hole-by-hole card expanded — set when it
  // is opened from the bar's "Check the card" prompt, which asks about pars
  // and stroke indexes in so many words.
  const [courseStatsShowHoles, setCourseStatsShowHoles] = useState(false);
  const openCourseStatsEdit = useCallback((tee, opts) => {
    setEditingTee(tee);
    setCourseStatsShowHoles(!!opts?.showHoles);
    setIsCourseStatsModalOpen(true);
  }, []);

  const teeForPlayer = useCallback((playerId) => {
    const id = playerTeeAssignments[playerId] ?? roundDefaultTeeId;
    return id != null ? teesById[id] : null;
  }, [playerTeeAssignments, roundDefaultTeeId, teesById]);

  // Is this round actually split across tees? Drives whether tee labels are
  // worth the row space — on a single-tee day they say nothing.
  const mixedTeesInRound = useMemo(() => {
    const ids = new Set(
      (roundData?.players || []).map(p => playerTeeAssignments[p.id] ?? roundDefaultTeeId)
    );
    return ids.size > 1;
  }, [roundData, playerTeeAssignments, roundDefaultTeeId]);

  // Who may correct the round's course data / allowance from the Setup tab.
  const canEditCourseStats = hasPermission(Permission.MANAGE_COURSES) || isSocietyOwner;
  const canEditAllowance = hasPermission(Permission.EDIT_TOURNAMENT) || isSocietyOwner || isRoundCreator;
  const canEditAnyStats = (canEditCourseStats || canEditAllowance) && !isCurrentRoundFinalized;

  // Team-round permissions — mirror the backend's own gates exactly (start
  // team rounds requires MANAGE_COMPETITIONS; finalize/reopen requires
  // FINALIZE_ROUND, same as the individual flow) so a button is never shown
  // to someone who'd just get a 403 from clicking it.
  const canStartTeamRounds = hasPermission(Permission.MANAGE_COMPETITIONS) || isSocietyOwner;
  const canFinalizeTeamRounds = hasPermission(Permission.FINALIZE_ROUND) || isSocietyOwner || isAdmin || isRoundCreator;

  // Finalising / ending / starting rounds is organiser-only. Members see status
  // and their own next step (score) instead of these controls.
  const canFinalizeRound = hasPermission(Permission.FINALIZE_ROUND) || isSocietyOwner || isAdmin || isRoundCreator;

  // Cross-round hand-off state. `hasNextRound` decides whether a finalised round
  // shows "Set up Round N+1" or the terminal "Competition complete" close-out;
  // `prevRoundFinalized` drives the next round's arrival banner.
  const hasNextRound = useMemo(
    () => roundOptions.some(r => r.sequence_number === selectedRound + 1),
    [roundOptions, selectedRound]
  );
  const nextRoundOption = useMemo(
    () => roundOptions.find(r => r.sequence_number === selectedRound + 1),
    [roundOptions, selectedRound]
  );
  const prevRoundFinalized = useMemo(
    () => !!roundOptions.find(r => r.sequence_number === selectedRound - 1)?.is_finalized,
    [roundOptions, selectedRound]
  );
  // A next round that is itself already finalised is not something to "set up" —
  // the competition is complete, so the hand-off card must read that way (e.g.
  // when viewing an earlier round after the whole tournament is done).
  const nextRoundStartable = hasNextRound && !nextRoundOption?.is_finalized;

  // THE COMPETITION BEING OVER IS NOT THE SAME AS THIS ROUND BEING DONE, and
  // the two want opposite things from the page.
  //
  // Once every round is finalised there is no workflow left: whichever tab you
  // land on, the answer is the same two destinations — the final board and the
  // report — so the close-out rides above the body on ALL of them.
  //
  // A finalised round in a competition still running is the opposite case. It
  // is a hand-off ("Round 1 finalised → set up Round 2"), it belongs to the
  // Finalise stage where the organiser just ended the round, and putting it on
  // the Players and Scoring tabs would push a forward action in front of
  // someone who came to those tabs to look at something else.
  const competitionFinalised = roundOptions.length > 0 && roundOptions.every(r => r.is_finalized);
  const showRoundHandoff = isCurrentRoundFinalized && !competitionFinalised && viewMode === 'grid';
  // Whichever of the two is on screen carries the Report button, so the
  // standalone report card stands down.
  const reportInHandoff = competitionFinalised || showRoundHandoff;

  // Show all players in the round for display
  const players = allPlayers;

  // Who sees the round action, and when. Unchanged from when this was a fixed
  // footer: an out-of-sync round announces itself on every tab, while End round
  // and Re-open round belong to the Finalise stage.
  const showSyncAction = !isTeamRoundDay && isRoundStarted && !isReviewingAdjustments
    && !isMobileLandscape && players.length > 0
    && (hasPermission(Permission.FINALIZE_ROUND) || isSocietyOwner || isAdmin || isRoundCreator)
    && (syncState === 'OUT_OF_SYNC' || syncState === 'SYNCING' || syncState === 'SYNCED' || isOnFinaliseTab);

  // Determine which players this user can edit scores for
  const editablePlayerIds = useMemo(() => {
    if (hasPermission(Permission.ENTER_ANY_SCORE) || isSocietyOwner) {
      return new Set(allPlayers.map(p => p.id));
    }
    const userPlayerId = myPlayerIdIn(userProfile, currentSociety?.id);
    if (!userPlayerId) return new Set();
    const userInRound = allPlayers.find(p => String(p.id) === String(userPlayerId));
    if (!userInRound || userInRound.group_number === null || userInRound.group_number === undefined) return new Set();
    return new Set(allPlayers.filter(p => p.group_number === userInRound.group_number).map(p => p.id));
  }, [allPlayers, hasPermission, isSocietyOwner, userProfile, currentSociety]);


  // Check if any visible players are missing a started round
  const playersMissingRound = useMemo(() => {
    return players.filter(p => !roundPlayerHandicaps[p.id]);
  }, [players, roundPlayerHandicaps]);

  // Roster players with no group slot for THIS round. Starting a round in this
  // state used to silently drop them into a lone group (or score a team round as
  // individuals), so the round must not start until they're assigned.
  const unassignedForRound = useMemo(
    () => players.filter(p => p.group_number === null || p.group_number === undefined),
    [players]
  );
  const canManageGroups = hasPermission(Permission.EDIT_TOURNAMENT) || isSocietyOwner || isRoundCreator;

  /**
   * Who may press Start Round.
   *
   * STARTING IS A WHOLE-FIELD ACT: `initiate_round` creates a Round for every
   * player on the roster and SNAPSHOTS each of their handicaps, so a round
   * started early fixes the field's handicaps at the wrong moment. It is not a
   * "begin my own game" button, which is how an unlabelled one reads.
   *
   * The button had no gate at all and was offered to any member who could see
   * the tee sheet — including people with no connection to the competition, for
   * whom the server answered 403 and the app showed a failure with no
   * explanation. This mirrors `scoring.initiate_round`'s own rule so the button
   * is offered exactly where it will work: an organiser, or somebody actually
   * playing in it.
   */
  const isPlayerInThisRound = useMemo(
    () => isPlayerInRoster(allPlayers, userProfile, currentSociety?.id),
    [allPlayers, userProfile, currentSociety],
  );

  const canStartRound = canManageGroups || isAdmin || isPlayerInThisRound;

  // Assign/edit this round's groups just-in-time — reuses the wizard's Groups
  // step scoped to the current round, so later rounds can be set after the
  // previous one finishes (dynamic pairings) rather than only up front.
  // ONE REPORT BUTTON, DEFINED ONCE. It appears in two places — paired with the
  // leaderboard inside the round-complete strip, and on its own above the body
  // on every other tab — and it must be the same object in both: same shell,
  // same size, same navy outline, same gold glyph. It was a small pill in the
  // header on two tabs and a card button on the third, which read as two
  // different features.
  const reportButton = useCallback((wide) => (
    <button
      type="button"
      className={`rcc-btn rcc-outline${wide ? ' rcc-btn--wide' : ''}${isBuildingReport ? ' rcc-btn--busy' : ''}`}
      onClick={handleDownloadReport}
      disabled={isBuildingReport}
      aria-label={canDownloadCompetition
        ? 'Competition report — full results, revisions and round insights'
        : `Round ${selectedRound} report — results, revisions and insights`}
    >
      {isBuildingReport ? (
        <><span className="report-row__spinner" />Preparing…</>
      ) : (
        <>
          {/* Gold on the glyph only — the report's own accent, kept from the
              card this replaced. */}
          <svg className="rcc-btn-icon" width="15" height="15" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12" /><path d="m7 12 5 5 5-5" /><path d="M5 21h14" />
          </svg>
          Competition report
        </>
      )}
    </button>
  ), [isBuildingReport, handleDownloadReport, canDownloadCompetition, selectedRound]);

  const handleAssignGroups = useCallback(() => {
    navigate('/tournaments/add', {
      state: { tournament: { id: selectedTournament }, initialStep: 5, initialRound: selectedRound },
    });
  }, [navigate, selectedTournament, selectedRound]);

  useEffect(() => {
    if (isRoundStarted && !isCurrentRoundFinalized && viewMode === null) {
      setViewMode('junction');
    } else if (isCurrentRoundFinalized && viewMode === null) {
      setViewMode('grid');
    }
  }, [isRoundStarted, isCurrentRoundFinalized, viewMode]);

  // Synchronize scores state whenever roundData changes
  useEffect(() => {
    if (roundData?.scores) {
      setScores(prev => ({
        ...roundData.scores
      }));
    }
  }, [roundData]);

  // Sync server-driven recalculation flag (course par/SI/slope/rating changed mid-round)
  useEffect(() => {
    if (roundData?.needs_recalculation && !roundData?.is_finalized) {
      setNeedsRecalculation(true);
    }
  }, [roundData?.needs_recalculation, roundData?.is_finalized]);

  // Scores feed the live board and the overall standings; both are cached, so
  // a score change has to knock them over or the board shows stale figures.
  const invalidateLeaderboards = useCallback(() => {
    queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
    queryClient.invalidateQueries(['leaderboard', selectedTournament]);
    queryClient.invalidateQueries(['matchplayLeaderboard', selectedTournament]);
  }, [queryClient, selectedTournament]);


  const [, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'

  // --- Autosave Logic ---
  useEffect(() => {
    if (!isRoundStarted || isCurrentRoundFinalized) return;
    
    const hasScores = Object.keys(scores).length > 0;
    if (!hasScores) return;

    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const filteredScores = {};
        const filteredCustomHandicaps = {};
        
        players.forEach(p => {
            if (!editablePlayerIds.has(p.id)) return;
            if (scores[p.id]) filteredScores[p.id] = scores[p.id];
            if (customHandicaps[p.id]) filteredCustomHandicaps[p.id] = customHandicaps[p.id];
        });

        if (Object.keys(filteredScores).length === 0) { setSaveStatus('saved'); return; }

        const response = await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/${selectedRound}/scores`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scores: filteredScores, custom_handicaps: filteredCustomHandicaps })
        });
        if (!response.ok) throw new Error('Failed to autosave');
        setSaveStatus('saved');
        // Every other mutation on this page refreshes the leaderboard; the
        // score save didn't, so the board kept showing pre-edit scores and
        // "thru" until its own 60s poll came round. A cleared score sitting
        // there afterwards is the obvious case, but entered scores lagged too.
        invalidateLeaderboards();
      } catch (error) {
        console.error("Autosave failed:", error);
        setSaveStatus('error');
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(timer);
  }, [scores, customHandicaps, selectedTournament, selectedRound, isRoundStarted, isCurrentRoundFinalized, players, authenticatedFetch, editablePlayerIds, invalidateLeaderboards]);

  const holeData = useMemo(() => {
    if (!course) return [];
    const newHoleData = [];
    const pars = course.hole_pars || [];
    const strokeIndices = course.hole_stroke_indices || [];
    for (let i = 0; i < 18; i++) {
      newHoleData.push({
        hole_number: i + 1,
        par: pars[i] !== undefined ? pars[i] : null,
        strokeIndex: strokeIndices[i] !== undefined ? strokeIndices[i] : null,
      });
    }
    return newHoleData;
  }, [course]);

  // ONE tee's card, hole by hole, in the shape holeData uses. Par and stroke
  // index belong to the tee, so on a mixed-tee day the scorecard grid cannot
  // describe the field with a single Par/SI pair — nor decide whether a score
  // was a birdie or a bogey against one. Falls back to the round's own card for
  // a tee carrying no data of its own.
  const holeDataForTee = useCallback((tee) => {
    const pars = tee?.hole_pars;
    if (!Array.isArray(pars) || pars.length !== 18) return holeData;
    const sis = tee?.hole_stroke_indices;
    return pars.map((par, i) => ({
      hole_number: i + 1,
      par,
      strokeIndex: Array.isArray(sis) && sis[i] != null ? sis[i] : holeData[i]?.strokeIndex,
    }));
  }, [holeData]);

  // Players grouped under the tee they played, in roster order, each section
  // carrying that tee's card. A single-tee round yields exactly ONE section, so
  // every grid below renders precisely what it always did.
  const teeSections = useMemo(
    () => groupPlayersByTee(players, p => teeForPlayer(p.id))
      .map(s => ({ ...s, holes: holeDataForTee(s.tee) })),
    [players, teeForPlayer, holeDataForTee]
  );

  const startingHole = useMemo(() => {
    const firstPlayerId = players[0]?.id;
    if (firstPlayerId && roundPlayerHandicaps[firstPlayerId]) {
      return roundPlayerHandicaps[firstPlayerId].starting_hole || 1;
    }
    return 1;
  }, [players, roundPlayerHandicaps]);

  // A true 9-hole day plays only the front or back nine; 18-hole days play all
  // 18. The finalize grid still shows all 18 columns (the played nine may be
  // the front or the back), but score entry is limited to the played nine.
  const dayHoles = roundData?.tournament?.holes || '18';

  const displayHoleData = useMemo(() => {
    if (!holeData || holeData.length === 0) return [];
    // The loop of holes actually played this day.
    let loop = holeData;
    if (dayHoles === 'front9') loop = holeData.slice(0, 9);
    else if (dayHoles === 'back9') loop = holeData.slice(9, 18);
    // Order from the starting hole, wrapping within the loop (shotgun).
    const index = loop.findIndex(h => h.hole_number === startingHole);
    if (index <= 0) return loop;
    return [...loop.slice(index), ...loop.slice(0, index)];
  }, [holeData, startingHole, dayHoles]);

  // The set of holes a player may enter scores for (the played nine, or 18).
  const playableHoles = useMemo(
    () => new Set(displayHoleData.map(h => h.hole_number)),
    [displayHoleData]
  );

  const playerTotals = useMemo(() => {
    const newTotals = {};
    players.forEach(player => {
      let f9G = 0, b9G = 0, f9S = 0, b9S = 0;
      let hasF9G = false, hasB9G = false, hasF9S = false, hasB9S = false;

      const playingHandicap = customHandicaps[player.id] !== undefined && customHandicaps[player.id] !== ''
        ? parseFloat(customHandicaps[player.id])
        : roundPlayerHandicaps[player.id]?.playing_handicap ?? 0;
      // Points depend on par AND stroke index, both properties of the tee this
      // player actually played — off a shared card their totals would be wrong
      // on a mixed-tee day.
      const holes = holeDataForTee(teeForPlayer(player.id));

      for (let i = 1; i <= 18; i++) {
        const score = scores[player.id]?.[i];
        const par = holes[i - 1]?.par;
        const si = holes[i - 1]?.strokeIndex;

        if (score !== '' && score !== undefined && score !== null) {
          if (i <= 9) { f9G += parseInt(score); hasF9G = true; }
          else { b9G += parseInt(score); hasB9G = true; }

          if (par && si) {
            const pts = calculatePoints(score, par, si, playingHandicap);
            if (pts !== '') {
              if (i <= 9) { f9S += pts; hasF9S = true; }
              else { b9S += pts; hasB9S = true; }
            }
          }
        }
      }
      newTotals[player.id] = {
        front9Gross: hasF9G ? f9G : '',
        back9Gross: hasB9G ? b9G : '',
        totalGross: (hasF9G || hasB9G) ? (f9G + b9G) : '',
        front9Stableford: hasF9S ? f9S : '',
        back9Stableford: hasB9S ? b9S : '',
        totalStableford: (hasF9S || hasB9S) ? (f9S + b9S) : '',
      };
    });
    return newTotals;
  }, [scores, players, holeDataForTee, teeForPlayer, customHandicaps, roundPlayerHandicaps]);

  // The one place the grid asks "what is this player playing off". Every branch
  // of the old grid re-derived this inline, four times over, and a fifth copy
  // in playerTotals — which is how a custom handicap could show on the card and
  // not in the total.
  const handicapFor = useCallback((playerId) => (
    customHandicaps[playerId] !== undefined && customHandicaps[playerId] !== ''
      ? parseFloat(customHandicaps[playerId])
      : roundPlayerHandicaps[playerId]?.playing_handicap ?? 0
  ), [customHandicaps, roundPlayerHandicaps]);

  // Which nines the day actually plays. A true nine-hole round offers its own
  // nine and the Total, not an empty Back 9 tab.
  //
  // Desktop has the width for all eighteen at once, so it collapses the two
  // nines into one tab rather than making someone click between halves of a
  // card that already fits. The key stays 'front' so the state is the same
  // state at either width — rotating a tablet must not strand the tab.
  const nineTabs = useMemo(() => {
    const parOf = (from, to) => teeSections[0]?.holes
      ?.slice(from, to).reduce((sum, h) => sum + (h?.par || 0), 0) || 0;
    const total = { key: 'total', label: 'Total', sub: `Par ${parOf(0, 18)}` };
    if (!isMobileView) return [{ key: 'front', label: 'Scorecard', sub: `Par ${parOf(0, 18)}` }, total];
    const front = { key: 'front', label: 'Front 9', sub: `Par ${parOf(0, 9)}` };
    const back = { key: 'back', label: 'Back 9', sub: `Par ${parOf(9, 18)}` };
    if (dayHoles === 'front9') return [front, total];
    if (dayHoles === 'back9') return [back, total];
    return [front, back, total];
  }, [teeSections, dayHoles, isMobileView]);

  // A nine-hole day starts on the nine it plays, not on a tab that isn't there.
  useEffect(() => {
    if (!nineTabs.some(t => t.key === nineTab)) setNineTab(nineTabs[0].key);
  }, [nineTabs, nineTab]);

  // Front/Back are natural swipe targets. Total is not — it is a different view
  // of the round, so it is reached by tapping, never by overshooting a swipe.
  const swipeStart = useRef(null);
  const handleGridTouchStart = useCallback((e) => {
    swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleGridTouchEnd = useCallback((e) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || nineTab === 'total') return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    // Horizontal intent only: the page below this card scrolls vertically, and
    // a diagonal drag must stay a scroll.
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.6) return;
    const nines = nineTabs.filter(t => t.key !== 'total').map(t => t.key);
    const at = nines.indexOf(nineTab);
    const next = dx < 0 ? at + 1 : at - 1;
    if (next >= 0 && next < nines.length) setNineTab(nines[next]);
  }, [nineTab, nineTabs]);

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const landscapeQuery = window.matchMedia('(orientation: landscape) and (max-height: 500px) and (max-width: 932px)');
    const handleMobile = (e) => setIsMobileView(e.matches);
    const handleLandscape = (e) => setIsMobileLandscape(e.matches);
    mobileQuery.addEventListener('change', handleMobile);
    landscapeQuery.addEventListener('change', handleLandscape);
    return () => {
      mobileQuery.removeEventListener('change', handleMobile);
      landscapeQuery.removeEventListener('change', handleLandscape);
    };
  }, []);

  // Clearing a hole writes through straight away rather than waiting for the
  // 2-second autosave. Any refetch of roundData in that window replaces the
  // whole scores map from the server (see the sync effect above), so a pending
  // clear could be silently reverted — which looked like "some players didn't
  // clear". Saving on the spot, and dropping the hole from the cached
  // roundData, closes that window.
  const clearHoleScore = async (playerId, holeIndex) => {
    setScores(prev => ({
      ...prev,
      [playerId]: { ...prev[playerId], [holeIndex]: '' },
    }));

    try {
      const res = await authenticatedFetch(
        `/tournaments/${selectedTournament}/rounds/${selectedRound}/scores`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scores: { [playerId]: { [holeIndex]: '' } } }),
        }
      );
      if (!res.ok) throw new Error('Failed to clear score');

      // Keep the cache honest: leaving the old value there means the next
      // refetch (or a window focus) puts the score straight back.
      queryClient.setQueryData(['roundData', selectedTournament, selectedRound], (old) => {
        if (!old?.scores) return old;
        const forPlayer = { ...(old.scores[playerId] || {}) };
        delete forPlayer[holeIndex];
        return { ...old, scores: { ...old.scores, [playerId]: forPlayer } };
      });
      invalidateLeaderboards();
    } catch (err) {
      console.error('Failed to clear score:', err);
      notify.error('Could not clear that score. Please try again.');
    }
  };

  const handleScoreChange = (playerId, holeIndex, value) => {
    setScores(prevScores => ({
      ...prevScores,
      [playerId]: {
        ...prevScores[playerId],
        [holeIndex]: value
      }
    }));
  };

  const handleCustomHandicapChange = (playerId, value) => {
    setCustomHandicaps(prev => ({ ...prev, [playerId]: value }));
  };

  // Reached only from the score-entry prompt below, i.e. once the round has
  // started and every player has a Round to write to. There is deliberately no
  // pre-start control here: before Start Round the starting hole lives on the
  // tee sheet (TournamentRoundGroup), which is what initiate_round reads, and a
  // field-wide edit from this page would flatten a shotgun's per-group holes.
  // Correcting it during play belongs on Enter Scores, which is group-scoped.
  const updateStartingHole = async (newStartHole) => {
    if (!players || players.length === 0) return;
    try {
        const updates = players.map(p => {
            const roundId = roundPlayerHandicaps[p.id]?.round_id;
            if (!roundId) return Promise.resolve();
            return authenticatedFetch(`/rounds/${roundId}/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ starting_hole: newStartHole })
            });
        });

        await Promise.all(updates);
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
    } catch (error) {
        console.error("Failed to update starting hole:", error);
        notify.error("Failed to update starting hole.");
    }
  };

  const handleStartRound = async () => {
    const selectedCourse = roundOptions.find(c => c.sequence_number === selectedRound);
    if (!selectedCourse) return notify.warn("Please select a valid course for the round.");

    if (isRollUp && !isTeeSheetFinalized) {
      return notify.warn("The tee sheet must be finalized before starting a roll-up round.");
    }

    try {
        const response = await authenticatedFetch('/initiate_round', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tournament_id: selectedTournament,
                course_id: selectedCourse.id,
                sequence_number: selectedRound,
                players_data: players.map(p => ({ 
                    player_id: p.id,
                    custom_handicap: customHandicaps[p.id] !== undefined && customHandicaps[p.id] !== '' ? parseInt(customHandicaps[p.id], 10) : null
                }))
            })
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to start round');      
        notify.success('Round started');
        setViewMode('junction');
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
    } catch (error) {
        notify.error(error.message);
    }
  };

  // Adds Round records for players on the tournament roster who have no Round for
  // the active (already-started) round. Used by the unassigned-players banner.
  // We can't route this through the wizard because PUT /groups silently skips
  // started rounds and the Groups step locks edits once a round has begun.
  const handleAddMissingPlayersToRound = async () => {
    if (playersMissingRound.length === 0) return;
    const selectedCourse = roundOptions.find(c => c.sequence_number === selectedRound);
    if (!selectedCourse) return notify.error('Course not found for this round.');
    try {
      const response = await authenticatedFetch('/initiate_round', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: selectedTournament,
          course_id: selectedCourse.id,
          sequence_number: selectedRound,
          players_data: playersMissingRound.map(p => ({ player_id: p.id, custom_handicap: null })),
        }),
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to add players to round');
      queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
      queryClient.invalidateQueries(['matchplayLeaderboard', selectedTournament]);
      queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
    } catch (error) {
      notify.error(error.message);
    }
  };

  const handleFinalizeRound = async () => {
    if (isFinalizing) return;
    setIsFinalizing(true);
    try {
        const response = await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/end`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                round_number: selectedRound,
                apply_handicap_adjustments: applyAdjustments && !isMatchplayRound,
            })
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to end round');

        setIsFinalizeConfirmOpen(false);
        setRoundsInReview(prev => prev.filter(num => num !== selectedRound));
        queryClient.invalidateQueries(['courses', selectedTournament]);

        // Check if next round has leaderboard pairing mode
        let showedPairings = false;
        try {
            const tournRes = await authenticatedFetch(`/tournaments/${selectedTournament}`);
            if (tournRes.ok) {
                const tournData = await tournRes.json();
                const nextRound = selectedRound + 1;
                const nextRoundConfig = tournData.groups?.find(rg => rg.round_number === nextRound);

                if (nextRoundConfig?.pairingMode === 'leaderboard') {
                    const lbRes = await authenticatedFetch(`/tournaments/${selectedTournament}/leaderboard_summary`);
                    if (lbRes.ok) {
                        const leaderboard = await lbRes.json();
                        if (leaderboard.length > 0) {
                            // Determine group size from current round's groups or default to 4
                            const currentGroups = tournData.groups?.find(rg => rg.round_number === selectedRound);
                            const currentGroupSize = currentGroups?.groups?.[0]?.players?.length || 4;

                            setChampionshipPairingsData({
                                leaderboard,
                                players: tournData.players || leaderboard,
                                groupSize: currentGroupSize,
                                nextRound,
                                tournamentId: selectedTournament
                            });
                            showedPairings = true;
                        }
                    }
                }
            }
        } catch (pairErr) {
            console.error('Championship pairings check failed:', pairErr);
        }

        if (!showedPairings) {
            if (applyAdjustments && !isMatchplayRound && isMobileView) {
                setIsWhatsAppShareModalOpen(true);
            } else {
                notify.success('Round ended');
                queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
            }
        }
    } catch (error) {
        notify.error(`Error finalizing round: ${error.message}`);
    } finally {
        setIsFinalizing(false);
    }
  };

  const handleConfirmPairings = async (groups) => {
    try {
        const payload = {
            all_rounds_groups: [{
                round_number: championshipPairingsData.nextRound,
                pairing_mode: 'leaderboard',
                groups: groups.map(g => ({
                    player_ids: g.players.map(p => p.id || p.player_id)
                }))
            }]
        };
        const res = await authenticatedFetch(`/tournaments/${championshipPairingsData.tournamentId}/groups`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `Server error ${res.status}`);
        }
        setChampionshipPairingsData(null);
        notify.success('Pairings saved');
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
    } catch (err) {
        notify.error(`Failed to save pairings: ${err.message}`);
    }
  };

  const handleEditPairings = (groups) => {
    const roundNum = championshipPairingsData.nextRound;
    setChampionshipPairingsData(null);
    navigate('/tournaments/add', {
        state: {
            tournament: { id: selectedTournament },
            initialStep: 5,
            initialRound: roundNum
        }
    });
  };

  const handleViewNextRoundGroups = async () => {
    try {
        const tournRes = await authenticatedFetch(`/tournaments/${selectedTournament}`);
        if (!tournRes.ok) return;
        const tournData = await tournRes.json();
        const nextRound = selectedRound + 1;
        const nextRoundConfig = tournData.groups?.find(rg => rg.round_number === nextRound);
        if (!nextRoundConfig) return;

        const pairingMode = nextRoundConfig.pairingMode || 'manual';
        const currentGroups = tournData.groups?.find(rg => rg.round_number === selectedRound);
        const currentGroupSize = currentGroups?.groups?.[0]?.players?.length || 4;

        // For leaderboard mode, fetch standings to enrich player data with positions/scores
        let leaderboard = [];
        if (pairingMode === 'leaderboard') {
            const lbRes = await authenticatedFetch(`/tournaments/${selectedTournament}/leaderboard_summary`);
            if (lbRes.ok) leaderboard = await lbRes.json();
        }

        // If the next round already has saved groups with players, show those
        const savedGroups = nextRoundConfig.groups?.filter(g => g.players?.length > 0) || [];
        if (savedGroups.length > 0) {
            // Build a lookup from leaderboard data
            const lbLookup = {};
            leaderboard.forEach(entry => {
                lbLookup[entry.player_id] = { position: entry.position, totalScore: entry.total_stableford ?? entry.total_nett };
            });

            setNextRoundPairingsData({
                presetGroups: savedGroups.map((g, i) => ({
                    id: g.id || i + 1,
                    name: g.name || `Group ${i + 1}`,
                    players: g.players.map(p => ({
                        ...p, id: p.id, name: p.name, handicap: p.handicap,
                        position: lbLookup[p.id]?.position,
                        totalScore: lbLookup[p.id]?.totalScore
                    }))
                })),
                pairingMode,
                nextRound,
                tournamentId: selectedTournament
            });
            return;
        }

        // Otherwise generate from leaderboard
        setNextRoundPairingsData({
            leaderboard,
            players: tournData.players || leaderboard,
            groupSize: currentGroupSize,
            pairingMode,
            nextRound,
            tournamentId: selectedTournament
        });
    } catch (err) {
        console.error('Failed to load next round groups:', err);
    }
  };

  const isRoundComplete = useMemo(() => {
    if (!players.length) return false;
    // Only the holes actually in play count — a true 9-hole day is complete
    // with its nine scored, not all 18.
    const holesInPlay = playableHoles.size
      ? Array.from(playableHoles)
      : Array.from({ length: 18 }, (_, i) => i + 1);
    return players.every(player =>
      holesInPlay.every(hole => {
        const score = scores[player.id]?.[hole];
        return score && score !== 0;
      })
    );
  }, [players, scores, playableHoles]);

  const handleEndRound = async () => {
    await handleEndRoundInternal();
  };

  const handleEndRoundInternal = async () => {
    // Force-save all scores regardless of current save state
    setSaveStatus('saving');
    try {
      const filteredScores = {};
      const filteredCustomHandicaps = {};
      players.forEach(p => {
          if (!editablePlayerIds.has(p.id)) return;
          if (scores[p.id]) filteredScores[p.id] = scores[p.id];
          if (customHandicaps[p.id]) filteredCustomHandicaps[p.id] = customHandicaps[p.id];
      });
      await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/${selectedRound}/scores`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scores: filteredScores, custom_handicaps: filteredCustomHandicaps })
      });
      setSaveStatus('saved');
    } catch (error) {
      console.error("Force save failed during end round:", error);
      if (!window.confirm("Failed to save final scores. Continue to end round anyway?")) return;
    }

    if (!isRoundComplete) {
      if (!window.confirm("Some scores are missing. Do you want to end the round anyway? Missing scores will be locked.")) {
        return;
      }
    }

    try {
        const response = await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/${selectedRound}/handicap_adjustments_preview`);
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch adjustments');
        setHandicapAdjustmentModalData(await response.json());
        setRoundsInReview(prev => [...new Set([...prev, selectedRound])]); // Add to review
    } catch (error) {
        notify.error(`Error fetching handicap adjustments preview: ${error.message}`);
    }
  };

  const handleReopenRound = async () => {
    if (!window.confirm("Are you sure you want to re-open this round?")) return;
    try {
        const response = await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/reopen_all`, {  
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sequence_number: selectedRound })
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to reopen');
        notify.success('Round re-opened');
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
    } catch (error) {
        notify.error(`Error re-opening round: ${error.message}`);
    }
  };

  // Switch the hub to another round and let the view-mode effect pick the right
  // screen for it (setup / junction / grid). Used by the progress rail and the
  // round-complete hand-off card so the "back → pick round → start" navigation
  // collapses to a single tap.
  const handleGoToRound = useCallback((num) => {
    if (num === selectedRound) return;
    setRoundsInReview(prev => prev.filter(n => n !== selectedRound));
    setViewMode(null);
    setSelectedRound(num);
    navigate(`?tournament=${selectedTournament}&round=${num}`);
  }, [selectedRound, selectedTournament, navigate]);

  const handleSetupNextRound = useCallback(
    () => handleGoToRound(selectedRound + 1),
    [handleGoToRound, selectedRound]
  );

  // STRAIGHT THERE, NO WAITING. This used to probe the server before it would
  // move: `/round-data` to see whether the day was a shared-ball one — 21KB,
  // because that endpoint eagerly loads every player's card — and then
  // `/scoring_groups`, both awaited, before navigating to the very screen that
  // asks those questions itself. On a weak connection the tab did nothing at
  // all for seconds, which is what had people pressing it again.
  //
  // Every branch of it ended at the same place. The picker detects a team day,
  // skips itself when there is only one group to score, and says so when there
  // are none — so the only thing worth doing here is going there.
  const handleLiveScoringClick = useCallback(() => {
    if (!selectedTournament) return;
    navigate(`/tournaments/${selectedTournament}/scorecard-input`,
      { state: { courseId: course?.id, courseSequence: selectedRound } });
  }, [navigate, selectedTournament, course, selectedRound]);

  const handleCellClick = useCallback((player, holeIndex) => {
    if (!editablePlayerIds.has(player.id)) return;
    // On a 9-hole day, only the played nine are enterable.
    if (!playableHoles.has(holeIndex)) return;
    // Check if we should prompt for starting hole
    const hasAnyScores = Object.values(scores).some(playerScores =>
        Object.values(playerScores).some(s => s !== null && s !== '' && s !== 0)
    );

    // This player's own card — in a mixed field the modal would otherwise name
    // the round tee's par and SI for someone playing a different one.
    const hole = holeDataForTee(teeForPlayer(player.id))[holeIndex - 1];
    const playingHandicap = customHandicaps[player.id] !== undefined && customHandicaps[player.id] !== '' 
      ? parseFloat(customHandicaps[player.id])
      : roundPlayerHandicaps[player.id]?.playing_handicap ?? 0;

    setSelectedCellData({ player, holeIndex, holeData: hole, playingHandicap });

    if (startingHole === 1 && !hasAnyScores && holeIndex !== 1) {
        setIsStartingHoleModalVisible(true);
    } else {
        setModalOpen(true);
    }
  }, [holeDataForTee, teeForPlayer, customHandicaps, roundPlayerHandicaps, scores, startingHole, editablePlayerIds, playableHoles]);

  const handleStartingHoleSelect = (hole) => {
    setIsStartingHoleModalVisible(false);
    updateStartingHole(hole);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCellData(null);
  };

  const handleScoreSelect = (score) => {
    if (selectedCellData) {
      const { player, holeIndex } = selectedCellData;

      let scoreToSet;
      if (score === 'CLEAR') {
        // Written through immediately — see clearHoleScore. Advancing to the
        // next hole would be wrong here: the user is correcting, not entering.
        clearHoleScore(player.id, holeIndex);
        setModalOpen(false);
        setSelectedCellData(null);
        return;
      }
      if (score === 'NS') {
        // NS writes a real gross of par + 2 + strokes, so the par has to be the
        // one this player actually played or the stored score is wrong.
        const hole = holeDataForTee(teeForPlayer(player.id))[holeIndex - 1];
        const playingHandicap = customHandicaps[player.id] !== undefined && customHandicaps[player.id] !== ''
          ? parseFloat(customHandicaps[player.id])
          : roundPlayerHandicaps[player.id]?.playing_handicap ?? 0;

        const handicapStrokes = getHandicapStrokes(playingHandicap, hole.strokeIndex);
        scoreToSet = (hole.par || 0) + 2 + handicapStrokes;
      } else {
        scoreToSet = score;
      }

      handleScoreChange(player.id, holeIndex, scoreToSet);

      // Advance to the next playable hole in the grid's natural column order
      // (1→18), not the shotgun play order — the finalize grid is laid out and
      // entered by hole number, so a shotgun group starting on hole 5 must
      // still spin hole 1 → hole 2. playableHoles limits this to the played
      // nine on a 9-hole day.
      let nextHoleNumber = null;
      for (let h = holeIndex + 1; h <= 18; h++) {
        if (playableHoles.has(h)) { nextHoleNumber = h; break; }
      }

      if (nextHoleNumber) {
        const nextHole = holeDataForTee(teeForPlayer(player.id))[nextHoleNumber - 1];
        const playingHandicap = customHandicaps[player.id] !== undefined && customHandicaps[player.id] !== ''
          ? parseFloat(customHandicaps[player.id])
          : roundPlayerHandicaps[player.id]?.playing_handicap ?? 0;
        setSelectedCellData({ player, holeIndex: nextHoleNumber, holeData: nextHole, playingHandicap });
      } else {
        handleCloseModal();
      }
    } else {
      handleCloseModal();
    }
  };

  const handleSaveCustomHandicaps = async () => {
    try {
        const response = await authenticatedFetch(`/tournaments/${selectedTournament}/rounds/${selectedRound}/custom_handicaps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ custom_handicaps: customHandicaps })
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to save handicaps');
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
        queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
        setNeedsRecalculation(true);
    } catch (error) {
        notify.error(`Error saving handicaps: ${error.message}`);
    }
  };

  // Permission to edit the player's stored handicap index (not just a one-off
  // playing-handicap override). Non-admins go straight to the playing-handicap
  // keypad, exactly as before.
  const canEditHandicapIndex = hasPermission(Permission.MANAGE_PLAYERS) || isSocietyOwner;

  const openHcpEdit = (player) => {
    if (canEditHandicapIndex) {
      setHcpChoicePlayer(player);
    } else {
      setHcpEditMode('playing');
      setHcpKeypadPlayer(player);
    }
  };

  const handleHcpEditChoice = (mode) => {
    setHcpEditMode(mode);
    setHcpKeypadPlayer(hcpChoicePlayer);
    setHcpChoicePlayer(null);
  };

  const handleHcpKeypadSave = async (value) => {
    if (!hcpKeypadPlayer) return;
    const playerId = hcpKeypadPlayer.id;
    const mode = hcpEditMode;
    setHcpKeypadPlayer(null);

    if (mode === 'index') {
      await handleSavePlayerIndex(playerId, value);
      return;
    }

    // Playing-handicap override (this round only)
    const updatedHandicaps = { ...customHandicaps, [playerId]: String(value) };
    setCustomHandicaps(updatedHandicaps);
    setNeedsRecalculation(true);
    try {
      const response = await authenticatedFetch(
        `/tournaments/${selectedTournament}/rounds/${selectedRound}/custom_handicaps`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ custom_handicaps: updatedHandicaps }) }
      );
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to save handicap');
      queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
      queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
    } catch (error) {
      notify.error(`Error saving handicap: ${error.message}`);
    }
  };

  const handleSavePlayerIndex = async (playerId, value) => {
    setNeedsRecalculation(true);
    // The index recompute replaces any one-off playing-handicap override.
    setCustomHandicaps(prev => {
      if (prev[playerId] === undefined) return prev;
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
    try {
      const response = await authenticatedFetch(
        `/tournaments/${selectedTournament}/rounds/${selectedRound}/player_index`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ player_id: playerId, handicap_index: value }) }
      );
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to update handicap index');
      queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
      queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
    } catch (error) {
      notify.error(`Error updating handicap index: ${error.message}`);
    }
  };

  const handleRecalculateRound = async () => {
    if (!selectedTournament || !selectedRound || isRecalculating) return;
    setIsRecalculating(true);
    try {
        const response = await authenticatedFetch(
            `/tournaments/${selectedTournament}/rounds/${selectedRound}/recalculate`,
            { method: 'POST' }
        );
        if (!response.ok) throw new Error((await response.json()).error || 'Recalculation failed');
        queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
        queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
        setNeedsRecalculation(false);
        setLastSyncedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        setRecalcSuccess(true);
        setTimeout(() => setRecalcSuccess(false), 800);
    } catch (error) {
        notify.error(`Error: ${error.message}`);
    } finally {
        setIsRecalculating(false);
    }
  };

  // Persist corrected course data (CR / Slope / Par) and/or allowance for this
  // round, then keep scores consistent. Course-data edits target the round's tee
  // when it has one, otherwise the course itself.
  const handleSaveCourseStats = async ({ courseChanges, allowance, affectsHandicaps }) => {
    // 1) Course Rating / Slope / Par
    if (course && Object.keys(courseChanges).length > 0) {
      // Target the tee the organiser actually tapped. Falls back to the
      // round's own tee for a single-tee round, and to the course record when
      // the round has no tee at all — the behaviour before mixed tees.
      const targetTeeId = editingTee?.id ?? course.tee_id;
      const body = targetTeeId
        ? { tees: [{ id: targetTeeId, ...courseChanges }] }
        : { ...courseChanges };
      const res = await authenticatedFetch(`/courses/${course.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update course');
    }

    // 2) Allowance (tournament-level)
    if (allowance !== undefined) {
      const res = await authenticatedFetch(`/tournaments/${selectedTournament}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchplay_percentage: allowance }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update allowance');
    }

    // 3) Keep the round consistent. Once underway, recompute scores; when CR,
    // Slope or Allowance changed, also recompute playing handicaps from each
    // player's stored index so the correction actually takes effect.
    if (isRoundStarted && !isCurrentRoundFinalized) {
      const res = await authenticatedFetch(
        `/tournaments/${selectedTournament}/rounds/${selectedRound}/recalculate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recompute_handicaps: !!affectsHandicaps }),
        }
      );
      if (!res.ok) throw new Error((await res.json()).error || 'Saved, but recalculation failed');
      setNeedsRecalculation(false);
      setLastSyncedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      // Say so. This path corrects the scores silently, so without the SYNCED
      // state it looks as though editing the course did nothing at all — the
      // out-of-sync warning never appears precisely because it's been handled.
      setRecalcSuccess(true);
      setTimeout(() => setRecalcSuccess(false), 1600);
    }

    queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]);
    queryClient.invalidateQueries(['liveLeaderboard', selectedTournament]);
    queryClient.invalidateQueries(['matchplayLeaderboard', selectedTournament]);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('player_names', JSON.stringify(players.map(p => p.name)));

    try {
        const response = await authenticatedFetch('/ocr/process-scorecard', {
            method: 'POST',
            body: formData,
        });

        if (response.ok) {
            const data = await response.json();
            setOcrData(data);
            setIsOCRModalOpen(true);
        } else {
            const errData = await response.json();
            notify.error(errData.error || 'Failed to scan scorecard. Please try again.');
        }
    } catch (error) {
        console.error("OCR Error:", error);
        notify.error("Failed to scan scorecard. Please try again.");
    } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // Reset input
        }
    }
  };

  const handleApplyOCRScores = (mappedScores) => {
    // mappedScores: [{ playerId, scores: { holeNum: score } }]
    setScores(prevScores => {
        const newScores = { ...prevScores };
        mappedScores.forEach(item => {
            const { playerId, scores: extractedScores } = item;
            if (!newScores[playerId]) newScores[playerId] = {};

            Object.entries(extractedScores).forEach(([holeNum, score]) => {
                if (score !== null) {
                    newScores[playerId][holeNum] = score;
                }
            });
        });
        return newScores;
    });
    setIsOCRModalOpen(false);
    setOcrData(null);
  };

  const renderPlayerCell = (player, playingHandicap, rowClass, landscape = false) => {
    const name = player.name || 'N/A';
    const parts = name.trim().split(/\s+/);

    let initials = '??';
    if (parts.length === 1) initials = parts[0].slice(0, 2).toUpperCase();
    else if (parts.length > 1) initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();

    return (
      <div className={`grid-label player-name-cell ${rowClass}`}>
        <div className="player-initials">{initials}</div>
        <div className="player-hcp-sub">({playingHandicap})</div>
      </div>
    );
  };

  const totalPar = useMemo(() => course?.hole_pars?.reduce((acc, par) => acc + (par || 0), 0) || 0, [course]);


  // Derive the playing handicap a given index would produce for this round's
  // course/tournament settings. Mirrors the server's compute_playing_handicap
  // and the inline player-setup calc, so we can preview it before a round is
  // initiated (when there's no stored round_player_handicaps entry yet).
  // Slope, Course Rating and par as ONE player sees them. All three are
  // properties of a tee, so in a mixed-tee round they differ between players in
  // the same group; without a playerId this falls back to the round's own tee,
  // which is what every single-tee day resolves to anyway.
  const ratingsForPlayer = useCallback((playerId) => {
    const tee = playerId != null ? teeForPlayer(playerId) : null;
    const teePar = Array.isArray(tee?.hole_pars) && tee.hole_pars.length
      ? tee.hole_pars.reduce((acc, p) => acc + (p || 0), 0)
      : null;
    return {
      slope: tee?.slope_rating ?? course?.slope_rating ?? 113,
      courseRating: tee?.course_rating ?? course?.course_rating ?? 0,
      par: teePar ?? totalPar ?? 0,
    };
  }, [teeForPlayer, course, totalPar]);

  const computePlayingHcpFromIndex = useCallback((index, playerId) => {
    if (index === null || index === undefined || index === '') return null;
    const whsIndex = Number(index);
    if (Number.isNaN(whsIndex)) return null;
    const useSimple = roundData?.tournament?.use_simple_handicap;
    const isMatchplay = roundData?.tournament?.format === 'Singles Matchplay' || roundData?.tournament?.format === 'Pairs Matchplay';
    const allowance = isMatchplay ? 1.0 : ((roundData?.tournament?.matchplay_percentage / 100.0) || 1.0);
    if (useSimple) {
      return Math.round(Math.round(whsIndex) * allowance);
    }
    const { slope, courseRating, par } = ratingsForPlayer(playerId);
    const courseHandicap = whsIndex * (slope / 113.0) + (courseRating - par);
    return Math.round(courseHandicap * allowance);
  }, [roundData, ratingsForPlayer]);

  // The handicap INDEX a player is shown against for this round: the one stored
  // on their Round once it has been started, else their live one. `players` on
  // the round-data payload is the live Player record, so a historic round used
  // to show an index that had since been revised beside a playing handicap
  // derived from the old one — two numbers on one row that cannot reconcile.
  const indexForRound = useCallback((player) => (
    player ? (roundPlayerHandicaps[player.id]?.handicap_index ?? player.handicap ?? null) : null
  ), [roundPlayerHandicaps]);

  // The card's five counts, each hole judged against the par of the tee its
  // player played (a mixed-tee field has more than one card).
  const landscapeStats = useMemo(() => countResults(players.flatMap(p => {
    const holes = holeDataForTee(teeForPlayer(p.id));
    return Array.from({ length: 18 }, (_, i) => ({
      strokes: scores[p.id]?.[i + 1],
      par: holes[i]?.par,
    }));
  })), [scores, players, holeDataForTee, teeForPlayer]);

  if (!currentSociety) return <div style={{padding:'40px', textAlign:'center'}}><h3>No Society</h3><button onClick={()=>navigate('/setup')}>Go to Setup</button></div>;

  return (
    <div className={`new-scorecard-entry-page ${isPageReady ? 'content-ready' : ''} ${isMobileLandscape ? 'landscape-fullscreen' : ''}`}>
      <MainMenu legacy />
      <div className="top-blue-bar">
        <HeaderProfile />
        <h1 className="leaderboard-title">
          <SocietyHeaderBadge />
          <div className="title-container">
            <div className="title-second-row">
              {!isRoundStarted && !isCurrentRoundFinalized ? 'Round Setup' : 'Full Scorecard'}
            </div>
          </div>
        </h1>
      </div>
      <HandicapEditChoiceModal
        isOpen={!!hcpChoicePlayer}
        playerName={hcpChoicePlayer?.name}
        currentPlayingHandicap={hcpChoicePlayer
          ? (customHandicaps[hcpChoicePlayer.id]
              ?? roundPlayerHandicaps[hcpChoicePlayer.id]?.playing_handicap
              ?? computePlayingHcpFromIndex(indexForRound(hcpChoicePlayer), hcpChoicePlayer.id))
          : null}
        currentIndex={indexForRound(hcpChoicePlayer)}
        onChoose={handleHcpEditChoice}
        onClose={() => setHcpChoicePlayer(null)}
      />

      <HandicapKeypadModal
        isOpen={!!hcpKeypadPlayer}
        label={hcpKeypadPlayer
          ? `${hcpKeypadPlayer.name} — ${hcpEditMode === 'index' ? 'Handicap Index' : 'Playing Hcp'}`
          : (hcpEditMode === 'index' ? 'Handicap Index' : 'Playing Hcp')}
        currentHandicap={hcpKeypadPlayer
          ? (hcpEditMode === 'index'
              ? indexForRound(hcpKeypadPlayer)
              : (customHandicaps[hcpKeypadPlayer.id]
                  ?? roundPlayerHandicaps[hcpKeypadPlayer.id]?.playing_handicap
                  ?? computePlayingHcpFromIndex(indexForRound(hcpKeypadPlayer), hcpKeypadPlayer.id)))
          : null}
        noDecimal={hcpEditMode !== 'index'}
        computePreview={hcpEditMode === 'index'
          ? (idx) => computePlayingHcpFromIndex(idx, hcpKeypadPlayer?.id)
          : undefined}
        previewLabel="Playing Hcp"
        onSave={handleHcpKeypadSave}
        onClose={() => setHcpKeypadPlayer(null)}
      />

      <HandicapOverrideModal
        isOpen={isHandicapModalOpen}
        onSave={async () => { await handleSaveCustomHandicaps(); setIsHandicapModalOpen(false); }}        
        onCancel={() => setIsHandicapModalOpen(false)}
        players={players}
        roundPlayerHandicaps={roundPlayerHandicaps}
        customHandicaps={customHandicaps}
        onCustomHandicapChange={handleCustomHandicapChange}
      />

      <HandicapAdjustmentConfirmationModal
        isOpen={isHandicapAdjustmentModalOpen}
        onConfirm={() => handleFinalizeRound(true)}
        onCancel={() => handleFinalizeRound(false)}
        roundPlayersData={handicapAdjustmentModalData}
      />

      <ChampionshipPairingsModal
        isOpen={!!championshipPairingsData}
        onClose={() => { setChampionshipPairingsData(null); queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]); }}
        leaderboard={championshipPairingsData?.leaderboard}
        players={championshipPairingsData?.players}
        groupSize={championshipPairingsData?.groupSize || 4}
        onEdit={handleEditPairings}
        onConfirm={handleConfirmPairings}
      />

      <ChampionshipPairingsModal
        isOpen={!!nextRoundPairingsData}
        onClose={() => setNextRoundPairingsData(null)}
        presetGroups={nextRoundPairingsData?.presetGroups}
        leaderboard={nextRoundPairingsData?.leaderboard}
        players={nextRoundPairingsData?.players}
        groupSize={nextRoundPairingsData?.groupSize || 4}
        pairingMode={nextRoundPairingsData?.pairingMode || 'manual'}
        onEdit={(hasPermission(Permission.EDIT_TOURNAMENT) || isSocietyOwner) ? (groups) => {
            const roundNum = nextRoundPairingsData.nextRound;
            setNextRoundPairingsData(null);
            navigate('/tournaments/add', {
                state: { tournament: { id: selectedTournament }, initialStep: 5, initialRound: roundNum }
            });
        } : undefined}
      />

      <WhatsAppShareModal
        isOpen={isWhatsAppShareModalOpen}
        onClose={() => { setIsWhatsAppShareModalOpen(false); queryClient.invalidateQueries(['roundData', selectedTournament, selectedRound]); }}
        roundPlayersData={handicapAdjustmentModalData}
        isMobileView={isMobileView}
      />
      <FinalizeConfirmModal
        isOpen={isFinalizeConfirmOpen}
        onConfirm={handleFinalizeRound}
        onCancel={() => setIsFinalizeConfirmOpen(false)}
        applyAdjustments={applyAdjustments}
        isMatchplay={isMatchplayRound}
        isFinalizing={isFinalizing}
      />
      {modalOpen && (
        <ScoreInputModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onScoreSelect={handleScoreSelect}
          holeData={selectedCellData?.holeData}
          playingHandicap={selectedCellData?.playingHandicap}
          calculateHoleHandicapStrokes={getHandicapStrokes}
          playerName={selectedCellData?.player?.name}
          holeNumber={selectedCellData?.holeIndex}
        />
      )}
      <StartingHoleModal
        isOpen={isStartingHoleModalVisible}
        onSelect={handleStartingHoleSelect}
        onClose={() => {
            setIsStartingHoleModalVisible(false);
            setModalOpen(true);
        }}
        currentHole={selectedCellData?.holeIndex}
      />

      {isCourseStatsModalOpen && (
      <CourseStatsEditModal
        isOpen={isCourseStatsModalOpen}
        onClose={() => { setIsCourseStatsModalOpen(false); setEditingTee(null); }}
        onSave={handleSaveCourseStats}
        course={course}
        tee={editingTee}
        allowance={roundData?.tournament?.matchplay_percentage || 100}
        // With several tees in play the bar's rows are separate controls: a tee
        // row edits that tee's figures, the allowance row edits the allowance.
        // A single-tee round keeps one modal carrying both.
        canEditCourse={canEditCourseStats && (courseTees.length <= 1 || !!editingTee)}
        canEditAllowance={canEditAllowance && (courseTees.length <= 1 || !editingTee)}
        roundStarted={isRoundStarted && !isCurrentRoundFinalized}
        initialShowHoles={courseStatsShowHoles}
      />
      )}
      <ScorecardOCRModal
        isOpen={isOCRModalOpen}
        onClose={() => setIsOCRModalOpen(false)}
        extractedData={ocrData}
        roundPlayers={players}
        onConfirm={handleApplyOCRScores}
      />
      {isUploading && (
        <div className="scanning-overlay">
          <div className="magic-circle">
            <FiCamera size={50} color="white" />
            <BsStars className="ai-stars-icon-large" size={24} color="#FFD700" />
            <div className="scan-line"></div>
          </div>
          <div className="scanning-text">Scanning Card</div>
          <div className="scanning-subtext">Analyzing scorecard data</div>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileUpload}
      />

      {/* --- ROUND BLOCK ---
          One card where there used to be three stacked ones (tournament card,
          round-selector card, report card) plus a rail. Same targets, same
          actions; ~150px instead of ~330, which is what puts the scorecard
          itself above the fold. */}
      <div className="control-centre-header">
        {/* Row A — what you're looking at. The Report button used to share
            this line; it moved into the body so it is the same control in the
            same place on every tab, which gives the tournament name the whole
            width back. */}
        <div className="ccx-row-top">
          <button
            type="button"
            className="ccx-tournament"
            onClick={() => setIsTournamentSelectorOpen(true)}
          >
            <span className="ccx-eyebrow">Competition</span>
            <span className="ccx-tournament-row">
              <span className="ccx-tournament-name">
                {roundData?.tournament?.name || tournaments.find(t => t.id === selectedTournament)?.name || 'Select Competition'}
              </span>
              <FiChevronDown className="ccx-tournament-chev" size={14} />
            </span>
          </button>

        </div>

        {/* Row B — the round rail. The ONLY round control, in the space the
            old rail and the old round-selector card took between them.

            TWO THINGS AT ONCE, and they are independent: what STATE each round
            is in, and which one you are looking at. Status is the fill — green
            done, blue running, plain grey still to come — and selection is the
            navy, so a glance answers "how far along is this competition" and
            "where am I in it" separately. They used to share one axis: every
            round you were not on was the same white pill whether it had been
            played or not, so a six-round competition looked identical on day
            one and day five.

            Scrolls rather than shrinking past a thumb once it runs long. */}
        {roundOptions.length > 1 && (
          <div className={`ccx-rail${roundOptions.length > 6 ? ' ccx-rail--scroll' : ''}`}>
            {roundOptions.map((rd) => {
              const isSelected = rd.sequence_number === selectedRound;
              const state = rd.is_finalized ? 'done' : (rd.is_started ? 'live' : 'todo');
              const status = rd.is_finalized ? 'finalised' : (rd.is_started ? 'in progress' : 'not started');
              return (
                <button
                  type="button"
                  key={rd.sequence_number}
                  className={`ccx-seg ccx-seg--${state}${isSelected ? ' ccx-seg--on' : ''}`}
                  onClick={() => handleGoToRound(rd.sequence_number)}
                  aria-current={isSelected ? 'true' : undefined}
                  aria-label={`Round ${rd.sequence_number}, ${status}`}
                >
                  <span className="ccx-seg-label">R{rd.sequence_number}</span>
                  {/* The mark carries the state for anyone who cannot separate
                      the fills — a tick for done, a dot for a round in play. */}
                  {rd.is_finalized
                    ? <FiCheck className="ccx-seg-tick" size={11} strokeWidth={3.5} />
                    : rd.is_started ? <span className="ccx-seg-dot" /> : null}
                </button>
              );
            })}
          </div>
        )}

        {/* Row C — where you're playing and where the round stands. The meta
            line states only what this page's payload actually knows: no tee
            time, because tee times live on the tee sheet and are not in
            round-data. */}
        <div className="ccx-row-course">
          <div className="ccx-course">
            <h2 className="ccx-course-name">{course?.name || 'Loading course…'}</h2>
            <span className="ccx-course-meta">
              {[
                `Round ${selectedRound}`,
                roundData?.tournament?.format || 'Stableford',
                `${players.length} player${players.length === 1 ? '' : 's'}`,
              ].join(' · ')}
            </span>
          </div>
          <span className={`ccx-chip ccx-chip--${isCurrentRoundFinalized ? 'done' : (isRoundStarted ? 'live' : 'soon')}`}>
            {isCurrentRoundFinalized ? 'Finalised' : (isRoundStarted ? 'In progress' : 'Upcoming')}
          </span>
        </div>

        {/* One competition, several documents.
            The picker only appears for a multi-round competition, and only
            after the row is tapped — the row keeps ONE affordance, which is
            what the design asks for, and the choice is presented at the
            moment it becomes a real question rather than as permanent
            chrome nobody with a single round would ever need. */}
        {reportPicker && (
          <div
            className="report-picker-scrim"
            role="presentation"
            onClick={() => setReportPicker(false)}
          >
            <div
              className="report-picker"
              role="dialog"
              aria-modal="true"
              aria-labelledby="report-picker-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="report-picker__title" id="report-picker-title">
                Which report?
              </div>
              <p className="report-picker__text">
                The whole competition, or one round on its own.
              </p>

              <button
                type="button"
                className="report-picker__opt report-picker__opt--primary"
                onClick={() => { setReportPicker(false); buildReport(null); }}
              >
                <span className="report-picker__opt-name">Full competition</span>
                <span className="report-picker__opt-sub">
                  {`Aggregate of ${roundOptions.length} rounds`}
                </span>
              </button>

              {roundOptions.map((r) => (
                <button
                  key={r.sequence_number}
                  type="button"
                  className="report-picker__opt"
                  onClick={() => {
                    setReportPicker(false);
                    buildReport(r.sequence_number);
                  }}
                >
                  <span className="report-picker__opt-name">
                    {`Round ${r.sequence_number}`}
                  </span>
                  <span className="report-picker__opt-sub">
                    {r.course_name || r.name || 'That round on its own'}
                  </span>
                </button>
              ))}

              <button
                type="button"
                className="report-picker__cancel"
                onClick={() => setReportPicker(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="scorecard-container">
        {/* --- PHASE 2: PHASE FLOW PER ROUND --- */}
        <div className="phase-flow-container">
          <div className="phase-steps">
            {(() => {
              // Determine which tab is currently shown
              const currentTab = (!isRoundStarted && !isCurrentRoundFinalized) ? 'setup'
                : viewMode === 'players' ? 'setup'
                : (isReviewingAdjustments && !isCurrentRoundFinalized) ? 'finalise'
                : (viewMode === 'grid') ? 'finalise'
                : 'scoring';

              return <>
                <div
                  className={`phase-step ${currentTab === 'setup' ? 'active' : (isRoundStarted || isCurrentRoundFinalized ? 'navigable' : 'future')} tappable`}
                  onClick={() => {
                    if (!isRoundStarted && !isCurrentRoundFinalized) return;
                    if (isReviewingAdjustments) setRoundsInReview(prev => prev.filter(num => num !== selectedRound));
                    setViewMode('players');
                  }}
                >
                  <div className="phase-dot">{currentTab === 'setup' ? '1' : <FiCheck size={12}/>}</div>
                  <span className="phase-label">Setup</span>
                </div>
                <div
                  className={`phase-step ${currentTab === 'scoring' ? 'active' : (isRoundStarted ? 'navigable' : 'future')} ${isRoundStarted ? 'tappable' : ''}`}
                  onClick={() => {
                    if (!isRoundStarted) return;
                    if (isReviewingAdjustments) setRoundsInReview(prev => prev.filter(num => num !== selectedRound));
                    setViewMode('junction');
                  }}
                >
                  <div className="phase-dot">{currentTab === 'scoring' ? '2' : (isRoundStarted ? <FiCheck size={12}/> : '2')}</div>
                  <span className="phase-label">Scoring</span>
                </div>
                <div
                  className={`phase-step ${currentTab === 'finalise' ? 'active' : (isRoundStarted ? 'navigable' : 'future')} ${isRoundStarted ? 'tappable' : ''}`}
                  onClick={() => {
                    if (!isRoundStarted) return;
                    if (isReviewingAdjustments) return;
                    setViewMode('grid');
                  }}
                >
                  <div className="phase-dot-wrapper">
                    <div className="phase-dot">{currentTab === 'finalise' ? '3' : (isCurrentRoundFinalized ? <FiCheck size={12}/> : '3')}</div>
                    {needsRecalculation && !isCurrentRoundFinalized && <span className="phase-dot-alert" />}
                  </div>
                  <span className="phase-label">Finalise</span>
                </div>
              </>;
            })()}
          </div>
        </div>

        {/* Unassigned-players banner — shown once the round is started and someone
            on the tournament roster has no group/Round for this round. Uses the
            premium navy-on-slate banner language shared with the wizard. */}
        {!isTeamRoundDay && isRoundStarted && !isCurrentRoundFinalized && playersMissingRound.length > 0 && (
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#F1F5F9',
            color: 'var(--brand-navy)',
            borderRadius: '12px',
            padding: '10px 14px 10px 18px',
            /* Its own gutter. It used to borrow one from .scorecard-container,
               which turned out to be CourseManager.css's. */
            margin: '0 16px 16px',
            overflow: 'hidden',
          }}>
            <span style={{
              position: 'absolute',
              left: 0, top: 8, bottom: 8,
              width: '4px',
              background: 'var(--brand-navy)',
              borderRadius: '2px',
            }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.35 }}>
              {playersMissingRound.length} unassigned for Round {selectedRound}
            </div>
            {(hasPermission(Permission.EDIT_TOURNAMENT) || isSocietyOwner) && (
              <button
                onClick={handleAddMissingPlayersToRound}
                style={{
                  flexShrink: 0,
                  background: 'var(--brand-navy)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '7px 14px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Add to round
              </button>
            )}
          </div>
        )}

        {/* ONE SLOT, DIRECTLY UNDER THE STAGE TABS, on every tab.
            The whole competition finalised puts the close-out here — the same
            strip and the same two destinations wherever you are, because there
            is no workflow left to be in the middle of. Otherwise the slot holds
            the report on its own, in the same card and the same shape, so the
            button never moves between tabs. */}
        {competitionFinalised ? (
          <div className={`round-complete-card${canFinalizeRound ? '' : ' round-complete-card--calm'}`}>
            <div className="rcc-strip">
              <span className="rcc-strip-gutter" aria-hidden="true" />
              <span className="rcc-copy">
                <span className="rcc-title">Competition complete</span>
                <span className="rcc-sub">
                  {canFinalizeRound ? 'Scores locked · handicaps updated.' : "That's a wrap — the final results are in."}
                </span>
              </span>
              <span className={`rcc-check${canFinalizeRound ? '' : ' rcc-check--blue'}`}>
                <FiCheck size={19} strokeWidth={3.5} />
              </span>
            </div>
            <div className="rcc-actions">
              <button
                className={`rcc-btn rcc-primary${showReportRow ? '' : ' rcc-btn--wide'}`}
                onClick={() => navigate(`/leaderboard?tournament=${selectedTournament}`)}
              >
                Final leaderboard
              </button>
              {showReportRow && reportButton(false)}
            </div>
          </div>
        ) : showReportRow && !reportInHandoff && (
          <div className="report-action-card">
            <div className="rcc-actions">
              {reportButton(true)}
            </div>
          </div>
        )}

        {/* THE ROUND-LEVEL HAND-OFF — this round is done, the competition
            is not. It stays HERE, in the Finalise body, because that is
            where the organiser just ended the round and the next thing to
            do is a forward action ("set up Round 2"). Putting it on Players
            or Scoring would push that in front of someone who came to those
            tabs to look at something else.

            Once every round is finalised this stops rendering and the
            close-out strip above the body takes over on all three tabs —
            see `competitionFinalised`.

            A strip, not a card: it was a ~200px centred block and it is the
            first thing on a finalised round, so it pushed the scorecard
            below the fold. The Report rides in the action row, so there is
            only ever one of it on screen (see `reportInHandoff`). */}
        {showRoundHandoff && (() => {
          const reportBtn = showReportRow ? reportButton(false) : null;

          // A lone action fills the row rather than sitting half-width with
          // a gap beside it.
          const pair = (primary) => (
            <div className="rcc-actions">
              {React.cloneElement(primary, {
                className: `${primary.props.className}${reportBtn ? '' : ' rcc-btn--wide'}`,
              })}
              {reportBtn}
            </div>
          );

          // There is not always a round to hand off TO. Viewing a finalised
          // last round while an earlier one is still open leaves the
          // competition unfinished with nothing forward from here — and
          // `nextRoundStartable` is what tells those apart, including the
          // case where the next round exists but is already finalised.
          const board = (label) => (
            <button className="rcc-btn rcc-primary" onClick={() => navigate(`/leaderboard?tournament=${selectedTournament}&live=true`)}>{label}</button>
          );

          return canFinalizeRound ? (
            <div className="round-complete-card">
              <div className="rcc-strip">
                <span className="rcc-strip-gutter" aria-hidden="true" />
                <span className="rcc-copy">
                  <span className="rcc-title">Round {selectedRound} finalised</span>
                  <span className="rcc-sub">Scores locked · handicaps updated.</span>
                </span>
                <span className="rcc-check"><FiCheck size={19} strokeWidth={3.5} /></span>
              </div>
              {nextRoundStartable ? (
                <div className="rcc-actions">
                  <button className="rcc-btn rcc-primary rcc-btn--wide" onClick={handleSetupNextRound}>Set up Round {selectedRound + 1} →</button>
                  <button className="rcc-btn rcc-tonal" onClick={handleViewNextRoundGroups}>Round {selectedRound + 1} groups</button>
                  {reportBtn || <button className="rcc-btn rcc-outline" onClick={() => navigate(`/leaderboard?tournament=${selectedTournament}&live=true`)}>Leaderboard</button>}
                </div>
              ) : pair(board('Leaderboard'))}
            </div>
          ) : (
            <div className="round-complete-card round-complete-card--calm">
              <div className="rcc-strip">
                <span className="rcc-strip-gutter" aria-hidden="true" />
                <span className="rcc-copy">
                  {nextRoundStartable && nextRoundOption?.is_started ? (
                    <>
                      <span className="rcc-title">Round {selectedRound + 1} is live</span>
                      <span className="rcc-sub">The next round has started — enter your scores.</span>
                    </>
                  ) : nextRoundStartable ? (
                    <>
                      <span className="rcc-title">Round {selectedRound} complete</span>
                      <span className="rcc-sub">Waiting for the organiser to start Round {selectedRound + 1}.</span>
                    </>
                  ) : (
                    <>
                      <span className="rcc-title">Round {selectedRound} complete</span>
                      <span className="rcc-sub">Scores locked — the competition is still running.</span>
                    </>
                  )}
                </span>
                <span className="rcc-check rcc-check--blue"><FiCheck size={19} strokeWidth={3.5} /></span>
              </div>
              {nextRoundStartable && nextRoundOption?.is_started
                ? pair(<button className="rcc-btn rcc-primary" onClick={handleSetupNextRound}>Go to Round {selectedRound + 1} →</button>)
                : pair(board('View leaderboard'))}
            </div>
          );
        })()}

        {/* THE ROUND ACTION, IN THE PAGE RATHER THAN OVER IT.
            It was a fixed glass bar pinned 65px up from the bottom, which left
            a strip of page between it and the bottom nav — so the scorecard
            scrolled through the gap and out the other side, behind a button.
            Inline, above the grid, it scrolls with everything else and there is
            no gap to see through.

            It keeps the cross-tab rule it had: a round that needs recalculating
            says so wherever you are, and End round / Re-open round belong to
            the Finalise stage. */}
        {showSyncAction && (
          <SyncActionFooter
            syncState={syncState}
            onRecalculate={handleRecalculateRound}
            onEndRound={handleEndRound}
            onReopen={handleReopenRound}
            lastSyncedAt={lastSyncedAt}
          />
        )}

        {/* --- PHASE 4: ROUND SETUP (HANDICAPS) --- */}
        {isTeamRoundDay ? (
          <TeamRoundPanel
            tournamentId={selectedTournament}
            roundNumber={selectedRound}
            teamCompetition={teamCompetition}
            teamRounds={teamRounds}
            isCurrentRoundFinalized={isCurrentRoundFinalized}
            viewMode={viewMode}
            setViewMode={setViewMode}
            canStart={canStartTeamRounds}
            canFinalize={canFinalizeTeamRounds}
            course={course}
            allowancePercent={roundData?.tournament?.matchplay_percentage}
            canEditCourseStats={canEditCourseStats}
            canEditAllowance={canEditAllowance}
            canEditHandicapIndex={canEditHandicapIndex}
            queryClient={queryClient}
            authenticatedFetch={authenticatedFetch}
            courseTees={courseTees}
          />
        ) : !isRoundStarted && !isCurrentRoundFinalized ? (
          <div className="round-setup-card">
            {prevRoundFinalized && selectedRound > 1 && (
              <div className="round-arrival-banner">
                Round {selectedRound - 1} complete · handicaps carried into Round {selectedRound}.
              </div>
            )}
            <h3 className="setup-title">Ready to Tee Off?</h3>
            <p className="setup-subtitle">Check handicaps and start the round</p>
            
            {course && (
              <>
                <CourseStatsBar course={course} totalPar={totalPar} allowancePercent={roundData?.tournament?.matchplay_percentage} editable={canEditAnyStats} onEditClick={openCourseStatsEdit} tees={courseTees} roundStarted={isRoundStarted} />
              </>
            )}

            {/* A ROLL UP CANNOT START UNTIL ITS TEE SHEET IS FINALISED, and until
                now the screen said so without offering a way to do it: the
                Start Round button went to 50% opacity reading "Finalize Tee
                Sheet First", while Scoring and Finalise stayed greyed out
                because the round genuinely has not started. That is a dead end
                — the whole page looks disabled and nothing on it moves you on.

                The sibling case below already solves this correctly: it names
                what is blocking and hands you the button that unblocks it. Same
                banner, same shape, pointing at the tee sheet. */}
            {isRollUp && !isTeeSheetFinalized && (
              <div className="group-assign-warning">
                <div className="gaw-body">
                  <span className="gaw-title">
                    <FiAlertCircle size={14} />
                    Tee sheet not finalised
                  </span>
                  <span className="gaw-sub">
                    Round {selectedRound} starts once the tee sheet is finalised — that's what fixes the groups and tee times.
                  </span>
                </div>
                {canManageGroups && (
                  <button
                    type="button"
                    className="gaw-btn"
                    onClick={() => navigate(`/tournaments/${selectedTournament}/teesheet`)}
                  >
                    Tee sheet
                  </button>
                )}
              </div>
            )}

            {!isRollUp && unassignedForRound.length > 0 && (
              <div className="group-assign-warning">
                <div className="gaw-body">
                  <span className="gaw-title">
                    <FiAlertCircle size={14} />
                    {unassignedForRound.length} player{unassignedForRound.length > 1 ? 's' : ''} not in a group
                  </span>
                  <span className="gaw-sub">Assign groups for Round {selectedRound} before starting — otherwise a player is left on their own.</span>
                </div>
                {canManageGroups && (
                  <button type="button" className="gaw-btn" onClick={handleAssignGroups}>Assign groups</button>
                )}
              </div>
            )}

            <div className="setup-actions-top" style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              {canStartRound && (() => {
                const startBlocked = (isRollUp && !isTeeSheetFinalized) || (!isRollUp && unassignedForRound.length > 0);
                const startLabel = (isRollUp && !isTeeSheetFinalized)
                  ? 'Finalize Tee Sheet First'
                  : (!isRollUp && unassignedForRound.length > 0 ? 'Assign Groups First' : 'Start Round');
                return (
                  <button
                    onClick={handleStartRound}
                    className="btn-primary"
                    disabled={startBlocked}
                    title={startBlocked ? (isRollUp ? 'Tee sheet must be finalized first' : 'Assign every player to a group first') : undefined}
                    style={{
                      padding: '0 24px',
                      height: '42px',
                      fontSize: '0.9rem',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(0, 119, 190, 0.25)',
                      border: 'none',
                      fontWeight: 600,
                      width: 'fit-content',
                      opacity: startBlocked ? 0.5 : 1,
                    }}
                  >
                    <FiCheck size={18} /> {startLabel}
                  </button>
                );
              })()}
            </div>
            
            <div className="player-setup-list">
              {players.map(player => {
                const whsIndex = player.handicap;
                const useSimple = roundData?.tournament?.use_simple_handicap;
                // Slope/CR/par are this player's tee's, not the round's — in a
                // mixed field the same index yields different handicaps, and
                // that is the rule rather than a bug.
                const standardPlayingHandicap = computePlayingHcpFromIndex(whsIndex, player.id);
                const playerTee = teeForPlayer(player.id);

                const customHcp = customHandicaps[player.id];
                const isOverridden = customHcp !== undefined && customHcp !== '' && parseFloat(customHcp) !== standardPlayingHandicap;
                const displayHcp = isOverridden ? parseFloat(customHcp) : standardPlayingHandicap;

                const initials = (player.name.split(' ').map(n => n[0]).join('')).toUpperCase().slice(0, 2);

                return (
                  <div key={player.id} className="player-setup-row">
                    <div className="player-info-brief">
                      <div className="player-avatar-circle" style={{ width: '38px', height: '38px', fontSize: '0.85rem' }}>{initials}</div>
                      <div className="player-name-stack">
                        <span className="p-name">{player.name}</span>
                        <span className="p-whs">
                          {useSimple ? 'HI' : 'WHS'} {whsIndex?.toFixed(1) || 'N/A'}
                          {/* Named only when the field is actually split, where
                              it is what explains two different handicaps off the
                              same index. On a single-tee day it is noise. */}
                          {mixedTeesInRound && playerTee ? ` · ${playerTee.name}` : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="hcp-edit-zone" style={{ gap: '12px' }}>
                      <div className="playing-hcp-display">
                        <div className="h-val" style={{ fontSize: '1.2rem' }}>{displayHcp}</div>
                        <div className="h-label" style={{ fontSize: '0.65rem' }}>Playing Hcp</div>
                      </div>
                      <button className="icon-btn" onClick={() => openHcpEdit(player)} style={{ padding: '4px' }}>
                        <FiEditIcon size={18} color="#0077be" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : viewMode === 'players' ? (
          <div className="round-setup-card">
            <h3 className="setup-title">Course & Players</h3>
            <p className="setup-subtitle">
              {isCurrentRoundFinalized
                ? 'The course and the handicaps used for this round'
                : 'The course and the playing handicaps for this round'}
            </p>

            {course && (
              <>
                <CourseStatsBar course={course} totalPar={totalPar} allowancePercent={roundData?.tournament?.matchplay_percentage} editable={canEditAnyStats} onEditClick={openCourseStatsEdit} tees={courseTees} roundStarted={isRoundStarted} />
              </>
            )}

            <div className="player-setup-list">
              {players.map(player => {
                // Historic rounds are reported off the index they were PLAYED
                // off — see indexForRound. The playing handicap below it is the
                // stored one, so the index has to be the stored one too.
                const whsIndex = indexForRound(player);
                const useSimple = roundData?.tournament?.use_simple_handicap;
                // Slope/CR/par are this player's tee's, not the round's — in a
                // mixed field the same index yields different handicaps, and
                // that is the rule rather than a bug.
                const standardPlayingHandicap = computePlayingHcpFromIndex(whsIndex, player.id);
                const playerTee = teeForPlayer(player.id);

                const customHcp = customHandicaps[player.id];
                const isOverridden = customHcp !== undefined && customHcp !== '' && parseFloat(customHcp) !== standardPlayingHandicap;
                // The fallback for a Round row that stored no playing handicap:
                // computed from the stored INDEX above, so it is historic too
                // rather than today's index run through today's course.
                const displayHcp = isOverridden ? parseFloat(customHcp) : standardPlayingHandicap;
                const roundHcp = roundPlayerHandicaps[player.id]?.playing_handicap;

                const initials = (player.name.split(' ').map(n => n[0]).join('')).toUpperCase().slice(0, 2);

                // Flag players on the tournament roster who have no group/Round for this
                // round (only meaningful once the round is started; pre-start, nobody has one yet).
                const isMissingFromRound = isRoundStarted && !roundPlayerHandicaps[player.id];

                return (
                  <div key={player.id} className="player-setup-row">
                    <div className="player-info-brief">
                      <div className="player-avatar-circle" style={{ width: '38px', height: '38px', fontSize: '0.85rem', opacity: isMissingFromRound ? 0.5 : 1 }}>{initials}</div>
                      <div className="player-name-stack">
                        <span className="p-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {player.name}
                          {isMissingFromRound && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              background: 'rgba(11, 19, 30, 0.08)',
                              color: 'var(--brand-navy)',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}>
                              <FiAlertCircle size={10} /> Unassigned
                            </span>
                          )}
                        </span>
                        <span className="p-whs">
                          {useSimple ? 'HI' : 'WHS'} {whsIndex?.toFixed(1) || 'N/A'}
                          {/* Named only when the field is actually split, where
                              it is what explains two different handicaps off the
                              same index. On a single-tee day it is noise. */}
                          {mixedTeesInRound && playerTee ? ` · ${playerTee.name}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="hcp-edit-zone" style={{ gap: '12px', opacity: isMissingFromRound ? 0.5 : 1 }}>
                      <div className="playing-hcp-display">
                        <div className="h-val" style={{ fontSize: '1.2rem' }}>{roundHcp ?? displayHcp}</div>
                        <div className="h-label" style={{ fontSize: '0.65rem' }}>Playing Hcp</div>
                      </div>
                      {!isCurrentRoundFinalized && !isMissingFromRound && (
                        <button className="icon-btn" onClick={() => openHcpEdit(player)} style={{ padding: '4px' }}>
                          <FiEditIcon size={18} color="#0077be" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>


          </div>
        ) : isReviewingAdjustments && !isCurrentRoundFinalized ? (
          <div className="round-setup-card">
            <h3 className="setup-title">{isMatchplayRound ? 'Finalise Match' : 'Review Adjustments'}</h3>
            <p className="setup-subtitle">
              {isMatchplayRound
                ? 'Matchplay does not revise handicaps'
                : 'Proposed handicap changes based on round results'}
            </p>

            {/* A match gets the reason instead of an empty table. The rules
                read a Stableford total, and a match's card is short by design
                — holes conceded, and the rest never played once it was won —
                so there is nothing here to review and nothing to switch on. */}
            {isMatchplayRound ? (
              <div className="handicap-review-list" style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                  Handicap adjustments come from a completed card, and a match
                  is not one — it ends when it is won and holes are given.
                  Finalising locks the scores and settles the match; every
                  player keeps the index they played off.
                </p>
              </div>
            ) : (
            <>
            <div className="handicap-review-list" style={{ marginBottom: '24px' }}>
              <table className="handicap-adjustment-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9', textAlign: 'left' }}>
                    <th style={{ padding: '10px 0', fontSize: '0.85rem', color: '#94a3b8' }}>Player</th>
                    <th style={{ padding: '10px 0', fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center' }}>Points</th>
                    <th style={{ padding: '10px 0', fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center' }}>New Hcp</th>
                  </tr>
                </thead>
                <tbody>
                  {handicapAdjustmentModalData.map(adj => (
                    <tr key={adj.player_id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '14px 0', fontWeight: 600, fontSize: '1rem' }}>{adj.name}</td>
                      <td style={{ padding: '14px 0', textAlign: 'center', fontWeight: 700, color: '#0077be', fontSize: '1.1rem' }}>{adj.stableford_score}</td>
                      <td style={{ padding: '14px 0', textAlign: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem', marginRight: '8px' }}>{adj.previous_handicap_index?.toFixed(1)} →</span>
                        <span style={{ fontWeight: 700, color: '#006747', fontSize: '1rem' }}>{adj.new_handicap_index?.toFixed(1)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {applyAdjustments && (
                <div style={{ fontSize: '0.7rem', color: '#006747', fontWeight: 600, marginTop: '12px', textAlign: 'center' }}>
                  Proposed changes will be applied when finalising.
                </div>
              )}
            </div>

            <div className="adjustment-settings-card">
              <div className="adjustment-settings-row">
                <div className="settings-text">
                  <span className="settings-label">Apply proposed changes</span>
                  <span className="settings-helper">Finalising locks scores and updates the leaderboard.</span>
                </div>
                <label className="ios-toggle">
                  <input 
                    type="checkbox" 
                    checked={applyAdjustments} 
                    onChange={(e) => setApplyAdjustments(e.target.checked)} 
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            </>
            )}

            <div className="setup-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setIsFinalizeConfirmOpen(true)}
                className="btn-primary"
                disabled={isFinalizing}
                style={{ width: '100%', height: '48px', fontSize: '1rem', borderRadius: '12px', fontWeight: 600 }}
              >
                {isFinalizing ? 'Finalising...' : 'Finalise Round'}
              </button>
              
              <button 
                onClick={() => setRoundsInReview(prev => prev.filter(num => num !== selectedRound))} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#94a3b8', 
                  fontSize: '0.9rem', 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  padding: '10px'
                }}
              >
                Back to Scorecard
              </button>
            </div>
          </div>
        ) : (isRoundStarted || isCurrentRoundFinalized) && viewMode === 'junction' ? (
          <div className="junction-container">
            <button className="junction-button primary" onClick={handleLiveScoringClick}>
              <div className="junction-button-icon">
                <FiEditIcon size={18} />
              </div>
              <div className="junction-button-content">
                <span className="junction-button-title">Enter Live Scores</span>
                <span className="junction-button-subtitle">
                  {hasPermission(Permission.ENTER_ANY_SCORE) || isSocietyOwner
                    ? "Competition-wide live entry"
                    : "Hole-by-hole entry for your group"}
                </span>
              </div>
              <div className="junction-button-arrow">
                <FiChevronRight size={18} />
              </div>
            </button>

            <button className="junction-button secondary" onClick={() => navigate(`/leaderboard?tournament=${selectedTournament}${selectedTournament ? '&live=true' : ''}`)}>
              <div className="junction-button-icon">
                <FiBarChart2 size={18} />
              </div>
              <div className="junction-button-content">
                <span className="junction-button-title">Go to Leaderboard</span>
                <span className="junction-button-subtitle">Live standings & results</span>
              </div>
              <div className="junction-button-arrow">
                <FiChevronRight size={18} />
              </div>
            </button>

            <button className="junction-button secondary" onClick={() => setViewMode('grid')}>
              <div className="junction-button-icon">
                <FiGrid size={18} />
              </div>
              <div className="junction-button-content">
                <span className="junction-button-title">
                  {isCurrentRoundFinalized 
                    ? 'View Full Scorecard' 
                    : ((hasPermission(Permission.FINALIZE_ROUND) || isSocietyOwner || isAdmin || isRoundCreator) ? 'Review and Finalize Scorecard' : 'View Scorecard')}
                </span>
                <span className="junction-button-subtitle">
                  {isCurrentRoundFinalized
                    ? 'See all hole-by-hole results'
                    : ((hasPermission(Permission.FINALIZE_ROUND) || isSocietyOwner || isAdmin || isRoundCreator) ? 'View, edit all scores and end the round' : 'Verify scores for your group')}
                </span>
              </div>
              <div className="junction-button-arrow">
                <FiChevronRight size={18} />
              </div>
            </button>

          </div>
        ) : (isRoundStarted || isCurrentRoundFinalized) && viewMode === 'grid' ? (
          <>
            <div className="scorecard-tile">

            <div className={`scorecard-grid-container${isRecalculating ? ' scorecard-grid-container--syncing' : ''}`}>
              {isLoading ? (
                <div>Loading...</div>
              ) : isMobileLandscape ? (
                <>
                <div className="landscape-title-bar">
                  <span className="landscape-title-text">
                    {roundData?.tournament?.name || 'Competition'} &middot; Round {selectedRound} &middot; {course?.name || ''}
                  </span>
                  <ScoreSummary counts={landscapeStats} inline className="landscape-stats" />
                </div>
                <div className={`landscape-grid ${isCurrentRoundFinalized ? 'disabled' : ''}`}>
                  <div className="grid-cell header-row">Hole</div>
                  {Array.from({ length: 18 }, (_, i) => <div key={`hole-${i + 1}`} className="grid-cell header-row">{i + 1}</div>)}
                  <div className="grid-cell total-label header-row">Out</div>
                  <div className="grid-cell total-label header-row">In</div>
                  <div className="grid-cell total-label header-row">Tot</div>
                  {teeSections.map((section) => (
                  <React.Fragment key={`ls-sec-${section.key}`}>
                  <div className="grid-cell par-row">{teeSections.length > 1 ? <TeeRowLabel tee={section.tee} /> : 'Par'}</div>
                  {Array.from({ length: 18 }, (_, i) => <div key={`par-${section.key}-${i + 1}`} className="grid-cell par-row">{section.holes[i]?.par || '0'}</div>)}
                  <div className="grid-cell total-value par-row">{section.holes.slice(0, 9).reduce((sum, hole) => sum + (hole?.par || 0), 0)}</div>
                  <div className="grid-cell total-value par-row">{section.holes.slice(9, 18).reduce((sum, hole) => sum + (hole?.par || 0), 0)}</div>
                  <div className="grid-cell total-value par-row">{section.holes.reduce((sum, hole) => sum + (hole?.par || 0), 0)}</div>
                  <div className="grid-cell si-row">SI</div>
                  {Array.from({ length: 18 }, (_, i) => <div key={`si-${section.key}-${i + 1}`} className="grid-cell si-row">{section.holes[i]?.strokeIndex || '-'}</div>)}
                  <div className="grid-cell si-row">-</div>
                  <div className="grid-cell si-row">-</div>
                  <div className="grid-cell si-row">-</div>
                  {section.players.map((player, playerIndex) => {
                    const playingHandicap = customHandicaps[player.id] !== undefined && customHandicaps[player.id] !== ''
                      ? parseFloat(customHandicaps[player.id])
                      : roundPlayerHandicaps[player.id]?.playing_handicap ?? 0;
                    const totals = playerTotals[player.id];
                    const rowClass = playerIndex % 2 === 0 ? 'player-row-even' : 'player-row-odd';
                    const pointsRowClass = playerIndex % 2 === 0 ? 'points-row-even' : 'points-row-odd';
                    return (
                    <React.Fragment key={`landscape-${player.id}`}>
                      {renderPlayerCell(player, playingHandicap, rowClass, true)}
                      {Array.from({ length: 18 }, (_, i) => (
                        <ScoreCell
                          key={`score-ls-${player.id}-${i + 1}`}
                          player={player}
                          holeNumber={i + 1}
                          score={scores[player.id]?.[i + 1]}
                          par={section.holes[i]?.par}
                          onClick={handleCellClick}
                          isMobile={true}
                          className={`${rowClass}${playableHoles.has(i + 1) ? '' : ' hole-not-in-play'}`}
                          readOnly={!editablePlayerIds.has(player.id) || !playableHoles.has(i + 1)}
                        />
                      ))}
                      <div className={`grid-cell total-value ${rowClass}`}>{totals?.front9Gross || '-'}</div>
                      <div className={`grid-cell total-value ${rowClass}`}>{totals?.back9Gross || '-'}</div>
                      <div className={`grid-cell total-value total-grand ${rowClass}`}>{totals?.totalGross || '-'}</div>
                      <div className={`grid-label points-label ${pointsRowClass}`}>Pts</div>
                      {Array.from({ length: 18 }, (_, i) => (
                        <PointsCell
                          key={`points-ls-${player.id}-${i + 1}`}
                          score={scores[player.id]?.[i + 1]}
                          par={section.holes[i]?.par}
                          strokeIndex={section.holes[i]?.strokeIndex}
                          playingHandicap={playingHandicap}
                          isMobile={true}
                          className={pointsRowClass}
                        />
                      ))}
                      <div className={`grid-cell total-value ${pointsRowClass}`}>{totals?.front9Stableford || '-'}</div>
                      <div className={`grid-cell total-value ${pointsRowClass}`}>{totals?.back9Stableford || '-'}</div>
                      <div className={`grid-cell total-value total-grand ${pointsRowClass}`}>{totals?.totalStableford || '-'}</div>
                    </React.Fragment>
                  )})}
                  </React.Fragment>
                  ))}
                </div>
                </>
              ) : (
                <>
                  {/* Front 9 / Back 9 / Total. Total is not a third nine — it
                      swaps the card for a ranked Out/In/Gross/Pts table, which
                      is the check an organiser actually wants before locking
                      the round. */}
                  <div className="fsc-tabs" role="tablist">
                    {nineTabs.map(t => (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={nineTab === t.key}
                        className={`fsc-tab${nineTab === t.key ? ' fsc-tab--active' : ''}`}
                        onClick={() => setNineTab(t.key)}
                      >
                        <span className="fsc-tab-label">{t.label}</span>
                        <span className="fsc-tab-sub">{t.sub}</span>
                      </button>
                    ))}
                  </div>

                  {nineTab === 'total' ? (
                    <FullScorecardTotals
                      players={players}
                      playerTotals={playerTotals}
                      handicapFor={handicapFor}
                    />
                  ) : (
                    <div
                      className="fsc-decks"
                      onTouchStart={handleGridTouchStart}
                      onTouchEnd={handleGridTouchEnd}
                    >
                      {/* One card per tee. Par and SI belong to the tee, so a
                          mixed-tee day cannot describe its field with a single
                          header band; a single-tee round yields exactly one
                          card and renders as the design draws it. */}
                      {teeSections.map(section => (
                        <FullScorecardGrid
                          key={`fsc-sec-${section.key}`}
                          section={section}
                          base={isMobileView && nineTab === 'back' ? 9 : 0}
                          holeCount={isMobileView ? 9 : 18}
                          scores={scores}
                          playerTotals={playerTotals}
                          handicapFor={handicapFor}
                          pointsFor={calculatePoints}
                          editableIds={editablePlayerIds}
                          playableHoles={playableHoles}
                          onCellClick={handleCellClick}
                          locked={isCurrentRoundFinalized}
                          showTeeLabel={teeSections.length > 1}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="scorecard-actions-bottom"></div>
          </div>
        </>
      ) : null}
    </div>

      {/* Team rounds have their own Finalise/Reopen button inside TeamRoundPanel
          and use team-specific endpoints — the individual-round sync footer
          (Recalculate/End Round/Reopen) doesn't apply and would double up. */}
      <BottomNav menuItems={[
        { name: 'Leaderboard', route: `/leaderboard?tournament=${selectedTournament}${selectedTournament ? '&live=true' : ''}`, icon: <FiBarChart2 size={24} /> },
        { name: 'Enter Scores', icon: <FiEditIcon size={24} />, onClick: handleLiveScoringClick,
          live: isRoundStarted && !isCurrentRoundFinalized },
        {
          name: 'Rounds',
          icon: <FiGrid size={24} />,
          onClick: () => {
            if (isRoundStarted || isCurrentRoundFinalized) {
              setViewMode(viewMode === 'junction' ? 'grid' : 'junction');
            }
          },
          active: true
        },
        { name: 'Manage', route: '/setup', icon: <FiSettings size={24} /> },
      ]} />

      <TournamentSelectorModal
        isOpen={isTournamentSelectorOpen}
        onClose={() => setIsTournamentSelectorOpen(false)}
        onSelect={(id) => {
            setSelectedTournament(id);
            setSelectedRound(1); // Reset to round 1 when tournament changes
            setRoundsInReview([]); // Clear all workflow states for new tournament
            // Update URL to match selection
            navigate(`?tournament=${id}&round=1`);
        }}
        selectedId={selectedTournament}
      />

    </div>
  );
};

export default NewScorecardEntry;